import {
  selectBuildingsDamage,
  selectCitiesCache,
  selectCurrentPlayer,
  selectFortressesUnderConstruction,
  selectGameType,
  selectGoblinsTrapDeathHexKeys,
  selectGoblinsTraps,
  selectHexes,
  selectLeprechaunsStealCoinsActions,
  selectPendingMovementActions,
  selectPlayers,
  selectRound,
  selectSpecialBuildings,
  selectTurnOrder,
  selectUnits
} from "../../../redux/reducers/sharedStateReducer/selectors";
import {
  addPendingMovementAction,
  applyGoblinsTrapMovement,
  leprechaunsStealCoins,
  moveUnit,
  showGoblinsTrapKillNotice
} from "../../../redux/actions";
import {resolveMovementWithTraps} from "../../movement/resolveMovementWithTraps";
import {isPlayerFaction} from "../../factions/isPlayerFaction";
import {FACTIONS} from "../../../constants/factions";
import {LEPRECHAUNS_UNIT_SIZE_FOR_1_COIN_THEFT} from "../../../configs/factions/leprechauns";
import {RATMEN_MELEE_COMBAT_BONUS_PER_ALLY} from "../../../configs/factions/ratmen";
import {integerDivision} from "../../other/integerDivision";
import {calculateAvailableMovementCells} from "../../gameState/calculateAvailableMovementCells";
import {getPlayerUnits} from "../../players/getPlayerUnits";
import {getNeighborEnemyUnits} from "../../unit/getNeighborEnemyUnits";
import {getUnitTypeConfig, UNIT_TYPES} from "../../../configs/units";
import {COMMON_BUILDING_TOKENS, SPECIAL_BUILDING_TOKENS, TERRAIN_TOKENS} from "../../../constants/tokens";
import {FINAL_ROUND} from "../../../configs/game";
import {
  calcBfsDistances,
  calcSupplyReachCells,
  calcSupplyReachCellsWithRelay,
  getBotGridRenderer,
  getNeighborHexKeys,
} from "../botGrid";
import {getPlayerFarms} from "../../gameState/getPlayerFarms";
import {getBotStrategy} from "../strategy/botStrategyStore";
import {getBotProfile} from "../strategy/getBotProfile";
import {getBotBonusProfile} from "../getBotBonusProfile";
import {
  BOT_BONUS_FRONTIER_PULL_MIN_UNIT_SIZE,
  BOT_BONUS_SWARM_FOCUS_ENEMY_EXTRA_DISTANCE,
  isBonusArmyPhase,
} from "../../../configs/botBonusBehavior";
import {ensureBonusAxis} from "../strategy/bonusAxis";
import {
  buildFrontierGarrisonIndex,
  buildPressureFactorMaps,
  calcFrontierGarrisonHold,
  calcMaxPressureFactor,
  collectStagingPullSources,
  findThreatenedGarrisonBarracksUnitIds,
  getPressureFactor,
  isBarracksGrowthReleased,
  pickSwarmFocus,
  registerPressureFactor,
} from "../evaluation/bonusMovement";
import {getFactionPreferences} from "../strategy/factionPreferences";
import {
  checkIfW42CelectialsStormPlayer,
  getBotExpectedMeleeDamage,
  getBotMovementDistanceHexes,
} from "../factions/celectialsBot";
import {
  calcCityGarrisonStrength,
  calcPlayerArmyStrength,
  calcStrongestOpponentArmy,
  findLateAggressionTargets
} from "../evaluation/armyStrength";
import {CAMPAIGN_TYPES, updateCampaigns} from "../strategy/campaigns";
import {getPlannerMode} from "../planner/plannerStore";
import {ensureRoundPlan} from "../planner/selectPlanByRollout";
import {getEnemyQuarterHexKeys, getOwnQuarterHexKeys} from "../evaluation/cities";
import {
  checkIfKeyPointToken,
  getControlledKeyPointHexKeys,
  getEnemyControlledKeyPoints,
  getEnemyKeyPointOwnerColors,
  getKeyPointValuePerRound
} from "../evaluation/keyPoints";
import {selectRuinsIncomeRoundLimit} from "../../gameState/income/checkIfRoundWithIncreasedRuinsIncome";
import {BOT_PRESSURE_APPROACH_WEIGHT, BOT_PRESSURE_TARGET_WEIGHT} from "../../../configs/botPressure";
import {isUnitInsectoid} from "../../factions/insectoids/isUnitInsectoid";
import {DEMONS_ABILITY_RANGE, DEMONS_AMOUNT_FACTOR} from "../../../configs/factions/demons";
import {isPlayerPolicy} from "../../factions/isPlayerPolicy";
import {POLICIES} from "../../../constants/policies";
import {keyToCoords} from "../../coords/keyToCoords";
import {BOT_EXPECTED_DICE_PENALTY, BOT_WEAK_UNIT_SIZE, BOT_WEIGHTS} from "../botConfig";
import {REGISTRY_SINGLETON} from "../../../scenes/Registry";
import {calcEnemyBlockedAssetValues} from "../evaluation/blockedAssets";
import {getCityHexKeys} from "../../gameState/getCityHexKeys";
import {getAllQuartersOfCity} from "../../gameState/getAllQuartersOfCity";
import {NEUTRAL_PLAYER_COLOR, NEUTRAL_PLAYER_ID} from "../../../constants/players";
import {isHegemonismBonusHexKey} from "../../policy/hegemonism/isHegemonismBonusHexKey";
import {POLICY_HEGEMONISM_BONUS_HEX_KEYS} from "../../../configs/policy/hegemonism";
import {FIELD_CENTRAL_HEX_KEY} from "../../../constants/field";
import {ELVES_SHOOTING_RANGE} from "../../../configs/factions/elves";
import {getPreferredUnitPrice} from "./decideRecruitmentStep";
import {getBotPredictions} from "../strongMode/botPredictionsStore";
import {getOwnInfluenceBuildings, projectInfluenceBuildingIncome} from "../evaluation/influencePaths";
import {
  isPreW32DefensePlayer,
  isPreW32MarchPlayer,
  isPreW32StallPlayer,
  isPreW32TargetOrderPlayer,
  isPreW35CampaignRangePlayer,
  isPreW35DefenseSuicidePlayer,
  isPreW37BarracksStayPlayer,
  isPreW37CampHoldPlayer,
  isPreW37ThreatHirePlayer,
  isWave27BehaviorPlayer,
} from "../abTestGates";
import {calcCityHolderBonus, CITY_THREAT_LEVELS, getCityCaptureThreats} from "../evaluation/cityCaptureThreat";
import {checkIfW36Feature} from "../evaluation/goldPlus";
import {checkIfW37ThisRoundThreat, getW37ThisRoundCityThreats} from "../evaluation/w37Threat";
import {
  buildW37BarracksInfo,
  calcW37GrowthDeltaAt,
  calcW37RecruitHolderBonus,
  calcW37ReserveHolderBonus,
  checkIfW37ReserveForCity,
  collectW37BarracksStay,
} from "../evaluation/w37Barracks";
import {getW37BarracksKnobs} from "../../../configs/botW37Barracks";
import {checkIfOnWorkingBarracks} from "../../gameState/checkIfOnWorkingBarracks";
import {
  checkIfAdjacentToWorkingMilitaryCamp
} from "../../gameState/specialBuildings/checkIfAdjacentToWorkingMilitaryCamp";
import {
  buildGarrisonUnitIndex,
  checkIfW35GarrisonPlayer,
  ensureGarrisonAssignments,
  findNearestAggressiveNeighbor,
  isGarrisonLockHard,
  isMeleeDoomed,
} from "../evaluation/garrisons";
import {
  W35_AGGRESSIVE_NEIGHBOR_DISTANCE,
  W35_CAPTURE_CAMPAIGN_FAR_SUPERIORITY,
  W35_CAPTURE_CAMPAIGN_MAX_DISTANCE,
  W35_FORCED_MARCH_PREFERRED_EXTRA_DISTANCE,
  W35_GARRISON_HOLD_BONUS,
  W35_GARRISON_STARVATION_LOSS,
} from "../../../configs/botGarrison";
import {checkIfFinalRound} from "../../checkIfFinalRound";
import {
  isSupplyGapMarchAllowed,
  W32_CAMPAIGN_MARCH_SUPPLY_PENALTY,
  W32_CAMPAIGN_MAX_STALL_ROUNDS,
  W32_CAMPAIGN_STALL_BLACKLIST_ROUNDS,
  W32_SUPPLY_RETURN_MAX_DISTANCE,
  W32_SUPPLY_RETURN_PER_CELL,
} from "../evaluation/logistics";
import {
  checkIfW39GiantUnit,
  checkIfW40GiantsAttackPlayer,
  getW39GiantReceivedDamageWeight,
  getW39GrowthMaxSize,
  scoreW39GiantPosition,
} from "../factions/giantsBot";
import {createW39SuccubesMovementContext, scoreW39SeducerPosition} from "../factions/succubesBot";
import {createW39GoblinsMovementContext, scoreW39GoblinPosition} from "../factions/goblinsBot";

// Гексагональная дистанция по осевым координатам (для локальных проверок радиуса)
const calcHexDistance = (cellKeyA, cellKeyB) => {
  const [aq, ar] = keyToCoords(cellKeyA);
  const [bq, br] = keyToCoords(cellKeyB);
  return (Math.abs(aq - bq) + Math.abs(ar - br) + Math.abs((aq + ar) - (bq + br))) / 2;
};

// Демоны в ранней игре не выбирают целями города этих агрессивных фракций.
// Сужен по итогам отчета №6 (широкий список делал демонов пассивными):
// орки и другие демоны — снова легитимные ранние цели
const DEMONS_EARLY_AVOID_FACTIONS = new Set([
  FACTIONS.RATMEN, FACTIONS.INSECTOIDS,
]);

// Волна 18 (живой тест 17.6): агрессивные фракции, которым позволена атака
// значительно более крупного нейтрала слабым (но >= 3) отрядом
const WEAK_NEUTRAL_ATTACK_FACTIONS = new Set([
  FACTIONS.BEASTMEN, FACTIONS.DEMONS, FACTIONS.INSECTOIDS, FACTIONS.ORCS, FACTIONS.RATMEN,
]);

// Волна 21 (живой тест 20.1): крысолюды при выборе города-цели раша избегают
// в приоритете фракций с собственной раш-доктриной (сложнее застать врасплох,
// сравнимая скорость мобилизации) — орки, крысолюды, инсектоиды
const RATMEN_RUSH_LOW_PRIORITY_OWNERS = new Set([
  FACTIONS.ORCS, FACTIONS.RATMEN, FACTIONS.INSECTOIDS,
]);

// Живой тест (после отчета №25): бюджет ферм (волна 28, 25.3) отключен для
// крысолюдов, инсектоидов и элементалей — крысолюды кормят рой прямо с ферм
// (найм = снабжение, кластеризация вокруг фермы — часть экономики, не баг),
// инсектоиды получают базовое снабжение бесплатно и так не читают этот флаг
// (isUnitInsectoid), элементали внесены по прямому запросу для отдельного A/B
const WAVE28_FARM_SUPPLY_BUDGET_EXCLUDED_FACTIONS = new Set([
  FACTIONS.RATMEN, FACTIONS.INSECTOIDS, FACTIONS.ELEMENTALS,
]);

const getExpectedMeleeDamage = (size) => Math.max(0, size - BOT_EXPECTED_DICE_PENALTY);

// Волна 35 (K): цель форсированного марша зверолюдов/крысолюдов. Предпочтительный
// владелец (неагрессивная фракция / не раш-доктрина) — только если его квартал в
// пределах дальности кампании или не дальше ближайшего квартала любого соперника
// больше чем на запас. Случай Вергилия (22.10, R7): три d6(6) шли на 14–16 клеток
// к неагрессивному соседу мимо пустого города Катона в 2 клетках
const pickW35ForcedMarchQuarter = (preferredQuarter, nearestQuarter, unitDistances) => {
  if (!preferredQuarter || !nearestQuarter) {
    return preferredQuarter || nearestQuarter;
  }
  const preferredDistance = unitDistances.get(preferredQuarter);
  const nearestDistance = unitDistances.get(nearestQuarter);
  return preferredDistance <= Math.max(
    W35_CAPTURE_CAMPAIGN_MAX_DISTANCE, nearestDistance + W35_FORCED_MARCH_PREFERRED_EXTRA_DISTANCE
  )
    ? preferredQuarter
    : nearestQuarter;
};

// Волна 32 (оборона): потеря размера отряда без снабжения в фазе снабжения
const W32_HOLDER_STARVATION_LOSS = 2;

/**
 * Волна 35 (G): гарнизон, которого держание города не спасает, на этом тике
 * ходит как обычный отряд — без фильтра клеток и надбавки гарнизона:
 *  - почти гарантированная гибель в собственном ближнем бою (isMeleeDoomed) и на
 *    месте, и на каждом доступном квартале своего города: город он не удержит, а
 *    противник получит очки за убийство (частичный шанс выжить — стоит);
 *  - голод: ни один квартал города не снабжается (supplyCells — с бюджетом ферм
 *    волны 28, если он действует), а отряд не переживет фазу снабжения. В
 *    последнем раунде фазы снабжения после хода нет.
 * Назначение (assignGarrisons) таких держателей и так не выбирает — здесь случаи,
 * когда обстановка изменилась после назначения или бюджет ферм занят другими
 */
const checkIfW35GarrisonReleased = ({unit, garrisonCity, cells, units, supplyCells, isFinalRound}) => {
  const isDoomedAt = (cellKey) => isMeleeDoomed(
    unit.size, getNeighborEnemyUnits(unit.ownerId, cellKey, units).map((enemy) => enemy.size)
  );
  if (isDoomedAt(unit.hexKey)
    && cells.every((cellKey) => !garrisonCity.quarters.has(cellKey) || isDoomedAt(cellKey))
  ) {
    return true;
  }
  return Boolean(supplyCells)
    && !isFinalRound
    && unit.size <= W35_GARRISON_STARVATION_LOSS
    && !isUnitInsectoid(unit)
    && ![...garrisonCity.quarters].some((quarterKey) => supplyCells.has(quarterKey));
};

// Волна 32 (логистика, L3): ячейка вне снабжения на маршруте захватной кампании отряда
// на квартал города, где марш через разрыв снабжения окупается захватом.
// context.w32CampaignTargetGarrisons задан только при активном L3 (у legacy null)
const isW32CampaignMarchCell = (unit, cellKey, context) => {
  const campaign = context.campaignByUnit?.get(unit.id);
  if (!campaign
    || campaign.type !== CAMPAIGN_TYPES.CAPTURE
    || !context.w32CampaignTargetGarrisons.has(campaign.targetHexKey)
  ) {
    return false; // только кампании на кварталы городов (руины и точки — как раньше)
  }
  const distances = context.campaignDistances?.get(campaign.targetHexKey);
  if (!distances) {
    return false;
  }
  return isSupplyGapMarchAllowed({
    unitSize: unit.size,
    distanceFromCell: distances.get(cellKey),
    distanceFromUnit: distances.get(unit.hexKey),
    targetGarrison: context.w32CampaignTargetGarrisons.get(campaign.targetHexKey),
    round: context.w32Round,
  });
};

// Давление ботов на сильного живого игрока рейтинговой партии: действия
// ПРОТИВ него получают множитель к оценке (см. src/configs/botPressure.js).
// Это вес, а не правило «все идут на лидера»: бот по-прежнему развивается,
// обороняется и не марширует через полкарты — просто при прочих равных
// выбирает вариант, который бьет по нему. В нерейтинговых партиях наборы
// целей под давлением всегда пусты, и множитель равен 1 везде.
// Волна 33 (M2): наборы стали картами «ключ → множитель» (getPressureFactor из
// evaluation/bonusMovement: map.get(key) ?? 1): игрок под фокусом дает прежние
// BOT_PRESSURE_TARGET_WEIGHT, ближайший противник оси бонусного бота — свой
// targetEnemyWeight, при пересечении — максимум. Для обычных ботов карты
// содержат ровно фокус, и множители равны прежним 1.6 / 1

/**
 * Оценка позиции отряда на ячейке cellKey (для сравнения вариантов перемещения между собой).
 */
const scoreUnitPosition = (unit, cellKey, context) => {
  const {
    units,
    hexes,
    currentPlayer,
    enemyQuarters,
    ownQuarters,
    targetDistances,
    ownQuartersUnderAttack,
    supplyReachCells,
    profile,
  } = context;

  let score = 0;

  // Волна 35 (G): отряд-гарнизон своего города ({cityKey, quarters}); у legacy и
  // у остальных отрядов undefined. Множитель давления на лидера на гарнизон не
  // действует: его дело — город, а не размен с фокусом (карты давления — null).
  // Гарнизон, отпущенный на этом тике (w35ReleasedGarrisonUnitId: обречен в своем
  // бою или голодает), оценивается как обычный отряд
  const garrisonCity = context.w35GarrisonByUnitId && context.w35ReleasedGarrisonUnitId !== unit.id
    ? context.w35GarrisonByUnitId.get(unit.id)
    : undefined;
  const pressureFactorByColor = garrisonCity ? null : context.pressureFactorByColor;
  const pressureFactorByCell = garrisonCity ? null : context.pressureFactorByCell;
  const pressureFactorByPlayerId = garrisonCity ? null : context.pressureFactorByPlayerId;
  // Волна 35 (D1): надбавка за захват на этой клетке — ее снимает захват без
  // выживания (ниже)
  let captureBonus = 0;
  // Бонус держателя своего города на этой клетке (волна 32): гарнизон в режиме
  // 'soft' получает ценность держателя не сверх него (ниже)
  let cityHolderBonus = 0;
  // Волна 37 (B1): «остаться» отряда-резервного держателя на своей казарме
  // (collectW37BarracksStay) — держатель G, волны 32 и обороны засчитывается и
  // здесь. У legacy и остальных ботов карты нет — null
  const w37StayEntry = context.w37BarracksStayByHexKey && cellKey === unit.hexKey
    ? context.w37BarracksStayByHexKey.get(cellKey)
    : undefined;
  const w37Reserve = w37StayEntry && w37StayEntry.unitId === unit.id && w37StayEntry.reserveCities.length > 0
    ? w37StayEntry
    : null;

  // Захват: встать на квартал чужого города (включая нейтральные).
  // Волна 22 (21.9b): нейтральный город, к которому уже есть путь влияния от
  // своей гильдии/храма, вентори/просвещенных до 11 раунда НЕ привлекает —
  // доход с него выше, пока он нейтрален (набор передан через контекст)
  if (enemyQuarters.has(cellKey)) {
    // Волна 25 (23.8): пустой город, на квартале которого уже стоит свой
    // отряд, не притягивает дополнительные — для захвата достаточно одного
    const isOversubscribedCapture = context.oversubscribedCaptureQuarters
      && context.oversubscribedCaptureQuarters.has(cellKey);
    if ((!context.discouragedCaptureQuarters || !context.discouragedCaptureQuarters.has(cellKey))
      && !isOversubscribedCapture
    ) {
      captureBonus = BOT_WEIGHTS.CAPTURE_ENEMY_QUARTER * profile.captureEagerness
        * getPressureFactor(pressureFactorByColor, enemyQuarters.get(cellKey)?.color);
      score += captureBonus;
    }
  } else if (supplyReachCells && !supplyReachCells.has(cellKey) && !isUnitInsectoid(unit)) {
    // Единственное оправдание позиции вне снабжения — поход на захват города.
    // Финальный подступ (смежно чужому/нейтральному кварталу) — мягкий штраф,
    // сопоставимый с потерей -2 размера; все остальное — запретительный штраф,
    // который не перебивает ни градиент кампании, ни убийство, ни оборона.
    // Инсектоиды всегда получают базовое снабжение — им голод не грозит.
    const isCaptureApproach = getNeighborHexKeys(cellKey, context.gridRenderer)
      .some((adjKey) => enemyQuarters.has(adjKey));
    // Волна 22 (21.12): город С ФЕРМОЙ снабдит захватившего сразу после
    // захвата — финальный подступ к такому городу голодом не штрафуется
    const isFarmCityApproach = context.farmCityQuarters
      && getNeighborHexKeys(cellKey, context.gridRenderer)
        .some((adjKey) => context.farmCityQuarters.has(adjKey));
    if (isFarmCityApproach) {
      // подступ к городу с фермой — без штрафа
    } else if (context.w32CampaignTargetGarrisons && isW32CampaignMarchCell(unit, cellKey, context)) {
      // Волна 32 (логистика): марш захватной кампании через разрыв снабжения.
      // Штраф (1) меньше шага градиента кампании (5) за вычетом штрафа пустой
      // ячейки (2): шаг с последней дороги конверта на пустую ячейку дает +2, иначе
      // ничья и хода нет. Ветка стоит до мягкого подступа — подступ к СВОЕЙ цели
      // тоже −1 вместо −9/−14 (локальный минимум у стен). Отряд, потерявший
      // разрешение (стоит, кампания снята), тянется обратно в снабжение (ниже)
      score -= W32_CAMPAIGN_MARCH_SUPPLY_PENALTY;
    } else if (isCaptureApproach) {
      score -= unit.size >= 4
        ? BOT_WEIGHTS.OUT_OF_SUPPLY_REACH_PENALTY_BIG_UNIT
        : BOT_WEIGHTS.OUT_OF_SUPPLY_REACH_PENALTY;
    } else if (context.cityThreatByQuarter
      && (context.cityThreatByQuarter.get(cellKey)?.level === CITY_THREAT_LEVELS.THIS_ROUND
        // Держатель города при риске следующего раунда терпит голод, только если
        // переживет фазу снабжения (−2): мертвый держатель город не удержит, а из
        // снабжения отряд успеет вернуться, когда риск станет риском этого раунда
        || (unit.size > W32_HOLDER_STARVATION_LOSS
          && Boolean(context.cityThreatByQuarter.get(cellKey)?.holders.some((holder) => holder.id === unit.id))))
      && calcCityHolderBonus(context.cityThreatByQuarter.get(cellKey), unit, cellKey) > 0
    ) {
      // Волна 32 (оборона): держатель квартала своего города, который иначе захватят
      // в ЭТОМ раунде (или отряд уже держит этот город), — голод (−2 размера в
      // следующей фазе снабжения) дешевле потери города, штраф мягкий. Риск
      // следующего раунда не выводит отряд из снабжения заранее
      score -= unit.size >= 4
        ? BOT_WEIGHTS.OUT_OF_SUPPLY_REACH_PENALTY_BIG_UNIT
        : BOT_WEIGHTS.OUT_OF_SUPPLY_REACH_PENALTY;
    } else if (context.w37RecruitHold && calcW37RecruitHolderBonus(
      context.w37RecruitHold.threatByQuarter.get(cellKey), unit, cellKey, context.w37RecruitHold.factor
    ) > 0) {
      // Волна 37 (B1/T): то же для держателя квартала города, до которого в этом
      // раунде дотягивается фантомный найм ходящего после бота
      score -= unit.size >= 4
        ? BOT_WEIGHTS.OUT_OF_SUPPLY_REACH_PENALTY_BIG_UNIT
        : BOT_WEIGHTS.OUT_OF_SUPPLY_REACH_PENALTY;
    } else if (garrisonCity && garrisonCity.quarters.has(cellKey) && unit.size > W32_HOLDER_STARVATION_LOSS) {
      // Волна 35 (G): гарнизон на квартале своего города терпит голод, как держатель
      // волны 32, если переживет фазу снабжения (−2): пустой город дороже размера
      score -= unit.size >= 4
        ? BOT_WEIGHTS.OUT_OF_SUPPLY_REACH_PENALTY_BIG_UNIT
        : BOT_WEIGHTS.OUT_OF_SUPPLY_REACH_PENALTY;
    } else {
      score -= BOT_WEIGHTS.OUT_OF_SUPPLY_HARD_PENALTY;
      // Волна 32 (логистика): отряд, уже стоящий вне снабжения без разрешения марша
      // (кампанию сняли: пропала готовность к агрессии, марш встал, сменился фокус),
      // получает градиент к конверту — плоские −80 держали его за 2+ ячейки от края,
      // пока голод не съедал отряд. У legacy w32SupplyReturnDistances = null
      if (context.w32SupplyReturnDistances && !supplyReachCells.has(unit.hexKey)) {
        score -= (context.w32SupplyReturnDistances.get(cellKey) ?? W32_SUPPLY_RETURN_MAX_DISTANCE + 1)
          * W32_SUPPLY_RETURN_PER_CELL;
      }
    }
  } else if (context.wave28FarmSupplyBudgetActive
    && context.budgetAwareSupplyReachCells
    && supplyReachCells?.has(cellKey)
    && !context.budgetAwareSupplyReachCells.has(cellKey)
    && !isUnitInsectoid(unit)
  ) {
    // Волна 28 (25.3): в геометрическом радиусе фермы, но вся ее вместимость
    // уже занята другими своими отрядами — снабжения по факту не будет
    // (common_problems_25: второй д10 эльфов на общей с первым ферме голодал
    // каждую фазу снабжения). Тот же штраф, что и мягкий тир полного
    // отсутствия досягаемости — это тоже некатастрофично (другой отряд может
    // уйти, новая ферма построиться), но статус-кво поощрять нельзя
    score -= unit.size >= 4
      ? BOT_WEIGHTS.OUT_OF_SUPPLY_REACH_PENALTY_BIG_UNIT
      : BOT_WEIGHTS.OUT_OF_SUPPLY_REACH_PENALTY;
  }

  // Блокировка ключевой точки противника: свой отряд на точке или смежно с ней
  // лишает владельца дохода этой точки каждый раунд
  if (context.enemyKeyPointBlockValues) {
    const blockValue = context.enemyKeyPointBlockValues.get(cellKey);
    if (blockValue) {
      score += blockValue * BOT_WEIGHTS.BLOCK_ENEMY_KEY_POINT_FACTOR * profile.military
        * getPressureFactor(pressureFactorByCell, cellKey);
    }
  }

  // Демоны: аура штрафа урона (-1 за каждые 5 суммарного размера демонов в радиусе 3
  // от отряда противника) — позиция рядом с противником усиливает ауру этим отрядом
  if (context.demonAuraMaps) {
    let enemiesInAuraRange = 0;
    context.demonAuraMaps.forEach((distanceMap) => {
      if (distanceMap.has(cellKey)) {
        enemiesInAuraRange += 1;
      }
    });
    if (enemiesInAuraRange > 0) {
      score += enemiesInAuraRange * (unit.size / DEMONS_AMOUNT_FACTOR) * BOT_WEIGHTS.DEMON_AURA_PER_ENEMY_UNIT;
    }
  }

  // Гномы с SERVARIANISM: отряд размера 7+ смежно своему руднику поднимает его
  // доход (+1 монета/раунд). Расставляем по РАЗНЫМ рудникам: покрытый другим
  // своим отрядом 7+ рудник повторной ценности не дает
  if (context.servarianismMineCells && unit.size >= 7) {
    let newlyCoveredMines = 0;
    getNeighborHexKeys(cellKey, context.gridRenderer).forEach((adjKey) => {
      if (!context.servarianismMineCells.has(adjKey)) {
        return;
      }
      const isCoveredByOther = units.some((otherUnit) =>
        otherUnit.id !== unit.id
        && otherUnit.ownerId === unit.ownerId
        && otherUnit.size >= 7
        && getNeighborHexKeys(otherUnit.hexKey, context.gridRenderer).includes(adjKey)
      );
      if (!isCoveredByOther) {
        newlyCoveredMines += 1;
      }
    });
    if (newlyCoveredMines > 0) {
      score += Math.min(2, newlyCoveredMines) * BOT_WEIGHTS.DWARVES_SERVARIANISM_MINE_GUARD;
    }
  }

  // Инсектоиды: на пустой ячейке отряд вообще не растет — теряется +1 базового
  // снабжения. Отряду у максимума размера рост уже не важен — двигается свободно.
  if (isUnitInsectoid(unit) && !hexes[cellKey]) {
    const insectoidMaxSize = getUnitTypeConfig(unit.type)?.maxSize || 0;
    if (unit.size < insectoidMaxSize - 1) {
      score -= BOT_WEIGHTS.INSECTOID_EMPTY_CELL_PENALTY;
    }
  }

  // Крысолюды: выросший d6 на своей ферме блокирует следующий найм за снабжение —
  // ему место на фронте, ферму освобождаем под нового рекрута.
  // Волна выходит уже с размера 3 (нюанс живого теста 11: рой мялся дома до 6 раунда)
  if (unit.factionId === FACTIONS.RATMEN
    && unit.size >= 3
    && context.ownFarmCells
    && context.ownFarmCells.has(cellKey)
  ) {
    score -= BOT_WEIGHTS.RATMEN_FARM_BLOCKING_PENALTY;
  }


  // Демоны: аура сильнее всего в гуще боев у центра карты, там же Великие руины —
  // постоянный градиент к центру
  if (context.isDemonsPlayer) {
    const [cellQ, cellR] = keyToCoords(cellKey);
    const distanceFromCenter = (Math.abs(cellQ) + Math.abs(cellR) + Math.abs(cellQ + cellR)) / 2;
    score -= distanceFromCenter * BOT_WEIGHTS.DEMON_CENTER_DRIVE_PER_CELL;
  }

  // Волна 22 (21.3): демоны агрессивно оспаривают неконтролируемые ключевые
  // точки центра (руины/великие руины/рудники в радиусе 4) — позиция на точке
  // или смежно ей ценна сама по себе: блокирует чужой захват и готовит свой
  if (context.isDemonsPlayer
    && context.uncontrolledCentralKeyPoints
    && context.uncontrolledCentralKeyPoints.size > 0
  ) {
    const isContestingCentralKeyPoint = context.uncontrolledCentralKeyPoints.has(cellKey)
      || getNeighborHexKeys(cellKey, context.gridRenderer).some(
        (adjKey) => context.uncontrolledCentralKeyPoints.has(adjKey)
      );
    if (isContestingCentralKeyPoint) {
      score += 10;
    }
  }

  // Волна 22 (21.16): пустая ячейка — не место стоянки: небольшой штраф
  // сдвигает отряд к строениям/точкам/целям кампаний при равных альтернативах
  // (живой тест 21: отряды раундами стояли на пустых ячейках у нейтральных
  // городов, которые никто не собирался захватывать)
  if (!hexes[cellKey]
    && !enemyQuarters.has(cellKey)
  ) {
    score -= 2;
  }

  // Оценка ближнего боя на этой позиции
  const adjacentEnemies = getNeighborEnemyUnits(unit.ownerId, cellKey, units);
  let expectedReceived = 0;
  if (adjacentEnemies.length > 0) {
    let myExpectedDamage = getBotExpectedMeleeDamage(unit, unit.ownerId);

    // Крысолюды: +2 урона за каждый смежный союзный отряд (суммарный бонус
    // по правилам не превышает размер самого отряда) — рой дерется вместе
    if (unit.factionId === FACTIONS.RATMEN) {
      const adjacentAllyUnits = [];
      getNeighborHexKeys(cellKey, context.gridRenderer).forEach((adjKey) =>
        units.forEach((otherUnit) => {
          if (otherUnit.id !== unit.id && otherUnit.ownerId === unit.ownerId && otherUnit.hexKey === adjKey) {
            adjacentAllyUnits.push(otherUnit);
          }
        })
      );
      myExpectedDamage += Math.min(unit.size, RATMEN_MELEE_COMBAT_BONUS_PER_ALLY * adjacentAllyUnits.length);

      // Волна 27 (24.1): позиция смежно союзникам, которые сами ведут ближний
      // бой, поднимает ИХ урон (+2 каждому по способности фракции) — кавалерия
      // встает так, чтобы бонус получало максимум дерущихся союзников
      if (context.ratmenAllySupportActive) {
        const engagedAlliesCount = adjacentAllyUnits.filter((allyUnit) =>
          getNeighborEnemyUnits(allyUnit.ownerId, allyUnit.hexKey, units).length > 0
        ).length;
        score += engagedAlliesCount * RATMEN_MELEE_COMBAT_BONUS_PER_ALLY * BOT_WEIGHTS.MELEE_TRADE_FACTOR;
      }
    }
    expectedReceived = adjacentEnemies.reduce(
      (sum, enemy) => sum + getBotExpectedMeleeDamage(enemy, unit.ownerId), 0
    );

    const killableEnemies = adjacentEnemies.filter((enemy) => enemy.size <= myExpectedDamage);
    if (killableEnemies.length > 0) {
      const bestKill = killableEnemies.reduce((best, enemy) => (enemy.size > best.size ? enemy : best));
      const killWinPoints = getUnitTypeConfig(bestKill.type)?.winPoints || 0;
      score += (BOT_WEIGHTS.MELEE_KILL + killWinPoints * BOT_WEIGHTS.MELEE_KILL_WIN_POINTS_FACTOR)
        * profile.military * getPressureFactor(pressureFactorByPlayerId, bestKill.ownerId);
    }

    // Волна 20 (19.9): катапульты противника — приоритетная цель ближнего боя
    // даже при невыгодном размене (собственная боевая ценность катапульты
    // низкая, а ее выбивание убирает угрозу стрельбы)
    if (adjacentEnemies.some((enemy) => enemy.type === UNIT_TYPES.D4.id)) {
      score += BOT_WEIGHTS.CATAPULT_TARGET_BONUS * profile.military;
    }

    // Волна 27 (24.2): эльфы-кайтеры — приоритетная цель ближнего боя: смежная
    // позиция навязывает бой до отхода. Бонус только отрядам, наносящим
    // хороший урон (3+ по матожиданию, включая бонус смежных союзников
    // крысолюдов, уже учтенный в myExpectedDamage) — слабым соваться незачем
    if (context.elfHuntActive
      && myExpectedDamage >= 3
      && adjacentEnemies.some((enemy) => enemy.factionId === FACTIONS.ELVES)
    ) {
      score += BOT_WEIGHTS.ELF_MELEE_HUNT_BONUS * profile.military;
    }

    // Бой с отрядами, блокирующими наши точки/строения: их выбивание возвращает
    // доход — приоритет пропорционален ценности блокируемого актива
    if (context.enemyBlockedAssetValues) {
      const blockedValueEngaged = adjacentEnemies.reduce(
        (sum, enemy) => sum + (context.enemyBlockedAssetValues.get(enemy.id) || 0), 0
      );
      if (blockedValueEngaged > 0) {
        score += blockedValueEngaged * BOT_WEIGHTS.ATTACK_BLOCKING_UNIT_FACTOR * profile.military;
      }
    }

    // Атака нейтралов при паритетных силах противника в 1 ходе рядом — ловушка:
    // потрепанный о нейтралов отряд и освобожденную точку заберет противник
    const isOnlyNeutralFight = adjacentEnemies.every((enemy) => enemy.ownerId === NEUTRAL_PLAYER_ID);
    if (isOnlyNeutralFight) {
      const nearbyEnemyStrength = units.reduce((sum, otherUnit) => {
        if (otherUnit.ownerId === unit.ownerId || otherUnit.ownerId === NEUTRAL_PLAYER_ID) {
          return sum;
        }
        return calcHexDistance(otherUnit.hexKey, cellKey) <= 2 ? sum + otherUnit.size : sum;
      }, 0);
      if (nearbyEnemyStrength >= unit.size) {
        score -= BOT_WEIGHTS.NEUTRAL_ATTACK_CONTESTED_PENALTY;
      }
    }

    // Осада: враг на квартале захватываемого города блокирует захват — выбиваем его.
    // Нюанс #2: без правдоподобного убийства осада защищенного города бессмысленна —
    // отряд получит урон и (часто) останется без снабжения.
    const isSiegePosition = adjacentEnemies.some((enemy) => enemyQuarters.has(enemy.hexKey));
    if (isSiegePosition && killableEnemies.length > 0) {
      score += BOT_WEIGHTS.SIEGE_CITY_GUARD * profile.military;
    } else if (isSiegePosition && supplyReachCells && !supplyReachCells.has(cellKey)) {
      score -= BOT_WEIGHTS.SUICIDE_PENALTY / 2;
    }

    // Эльфы ценят размер отряда выше всех (медленное восполнение) — ожидаемый
    // полученный урон весит для них в полтора раза больше. Волна 39: великан —
    // многораундовые вложения снабжения и 5 очков противнику
    const receivedWeight = unit.factionId === FACTIONS.ELVES
      ? 1.5
      : (checkIfW39GiantUnit(unit) ? getW39GiantReceivedDamageWeight(unit) : 1);
    // Урон, наносимый игроку под давлением ботов, весит больше — размен с ним
    // выгоден даже там, где с остальными бот бы его не принял (наибольший
    // множитель среди смежных: у обычного бота это прежние 1.6, если хоть один
    // из них под фокусом, иначе 1)
    const dealtWeight = calcMaxPressureFactor(pressureFactorByPlayerId, adjacentEnemies);
    score += (myExpectedDamage * dealtWeight - expectedReceived * receivedWeight)
      * BOT_WEIGHTS.MELEE_TRADE_FACTOR;

    // Самоубийственная позиция без захвата — жесткий штраф.
    // Волна 35 (D1): и на чужом квартале — захват отрядом, который по матожиданию
    // погибает в собственном бою, не состоится (бонус захвата снят ниже), а
    // выживаемый захват (урон меньше размера) сюда не попадает. Случай Арташира
    // (17.10, R7): d12(9) против ожидаемых 11 урона ушел с единственного квартала
    // -10,5 за «захватом» 187.8 и погиб
    if (expectedReceived >= unit.size && (!enemyQuarters.has(cellKey) || context.w35SuicideActive)) {
      score -= BOT_WEIGHTS.SUICIDE_PENALTY;
    }
    if (context.w35SuicideActive && captureBonus > 0 && expectedReceived >= unit.size) {
      score -= captureBonus;
      captureBonus = 0;
    }

    // Слабым отрядам не место в зоне контроля противника
    if (unit.size <= BOT_WEAK_UNIT_SIZE && !enemyQuarters.has(cellKey)) {
      score -= BOT_WEIGHTS.WEAK_UNIT_THREATENED_PENALTY;
    }

    // Волна 18 (запечена, живой тест 17.6): атака значительно более крупного
    // нейтрала отрядом меньшего размера — только агрессивным фракциям / с
    // MILITARISM|BARBARISM и только отрядом размера >= 3
    if (!enemyQuarters.has(cellKey)) {
      const strongerNeutralAdjacent = adjacentEnemies.some((enemy) =>
        enemy.ownerId === NEUTRAL_PLAYER_ID && enemy.size >= unit.size + 2);
      if (strongerNeutralAdjacent && (unit.size < 3 || !context.weakNeutralAttackAllowed)) {
        score -= BOT_WEIGHTS.SUICIDE_PENALTY;
      }
    }

    // Волна 22 (21.4/21.13): бой с нейтралом — только при строгом размерном
    // преимуществе: суммарный размер своих отрядов, смежных нейтралу (включая
    // этот), должен ПРЕВЫШАТЬ размер нейтрального отряда (бой при смежности
    // обязателен, нейтрал в защите почти всегда наносит урон). Действует для
    // всех фракций, включая позиции захвата нейтральных кварталов; исключение —
    // оборона квартала СВОЕГО города.
    // Волна 22.1 (отчет №18 §4.1): гарнизон города-фокуса роя (инсектоиды/
    // крысолюды) исключен — цель фокуса уже проверена на взятие целиком
    // (isCityTakeable: гарнизон ≤ армии роя), по-юнитная проверка стопорила
    // подтягивающихся к осаде по одному
    if (!ownQuarters.has(cellKey)) {
      const hasNoAdvantageNeutralFight = adjacentEnemies.some((enemy) => {
        if (enemy.ownerId !== NEUTRAL_PLAYER_ID) {
          return false;
        }
        if (context.cityFocusQuarters && context.cityFocusQuarters.has(enemy.hexKey)) {
          return false;
        }
        const alliedAdjacentTotal = units.reduce((sum, otherUnit) => (
          otherUnit.id !== unit.id
          && otherUnit.ownerId === unit.ownerId
          && calcHexDistance(otherUnit.hexKey, enemy.hexKey) <= 1
            ? sum + otherUnit.size
            : sum
        ), unit.size);
        return alliedAdjacentTotal <= enemy.size;
      });
      if (hasNoAdvantageNeutralFight) {
        score -= BOT_WEIGHTS.SUICIDE_PENALTY;
      }
    }

    // Волна 22 (21.5): позиция при крайнем перевесе смежного противника
    // запрещена даже ради захвата — «1 нападает на 5» гибнет без пользы,
    // квартал тут же отбивается. Оборона квартала своего города — исключение.
    // Волна 22.1 (отчет №18 §4.4): перевес считается по ЛОКАЛЬНОМУ балансу —
    // враги против отряда ВМЕСТЕ с его смежными союзниками: скоординированный
    // рой из 5 отрядов против одного крупного — не «1 нападает на 5», прежняя
    // по-юнитная проверка глушила совместные штурмы (крысолюды/инсектоиды).
    // Гарнизон города-фокуса роя исключен по той же логике, что и в 21.4/21.13
    if (!ownQuarters.has(cellKey)) {
      const adjacentEnemiesTotalSize = adjacentEnemies.reduce((sum, enemy) => (
        context.cityFocusQuarters
        && enemy.ownerId === NEUTRAL_PLAYER_ID
        && context.cityFocusQuarters.has(enemy.hexKey)
          ? sum
          : sum + enemy.size
      ), 0);
      const alliedLocalTotal = units.reduce((sum, otherUnit) => (
        otherUnit.id !== unit.id
        && otherUnit.ownerId === unit.ownerId
        && calcHexDistance(otherUnit.hexKey, cellKey) <= 1
          ? sum + otherUnit.size
          : sum
      ), unit.size);
      if (adjacentEnemiesTotalSize >= alliedLocalTotal * 3) {
        score -= BOT_WEIGHTS.SUICIDE_PENALTY * 2;
      }
    }
  }

  if (context.cityThreatByQuarter) {
    // Волна 32 (оборона): враг на квартале — +30 только кварталам ЭТОГО города
    // и позициям рядом с врагом, а не кварталам всех городов
    if (ownQuartersUnderAttack.size > 0) {
      const isDefendingAttackedCity = context.attackedCityQuarters.has(cellKey)
        || adjacentEnemies.some((enemy) => ownQuartersUnderAttack.has(enemy.hexKey));
      if (isDefendingAttackedCity) {
        score += BOT_WEIGHTS.DEFEND_OWN_CITY;
      }
    }
    // Держатель города: захвата нет, пока свой отряд стоит на ЛЮБОМ квартале.
    // Бонус получает первый держатель (и при оценке «остаться»), второй — только
    // если первые не переживут урон ходящих после бота. Бонуса нет, если отряд
    // по матожиданию погибнет в собственном ближнем бою бота (бой в фазе
    // владельца обязателен): мертвый держатель город не удержит
    const cityThreat = context.cityThreatByQuarter.get(cellKey);
    if (cityThreat && expectedReceived < unit.size) {
      cityHolderBonus = calcCityHolderBonus(cityThreat, unit, cellKey);
      score += cityHolderBonus;
    }
    // Волна 37 (B1): резервный держатель на своей казарме получает бонус держателя
    // как на смежном свободном квартале — при риске следующего раунда «остаться» и
    // «встать на квартал» равны, и рост на казарме решает в пользу казармы
    if (w37Reserve && expectedReceived < unit.size) {
      cityHolderBonus = calcW37ReserveHolderBonus(w37Reserve, unit);
      score += cityHolderBonus;
    }
    // Волна 37 (B1/T): угроза этого раунда только фантомным наймом ходящего после
    // бота — держатель квартала, как при угрозе отрядами (не сверх бонуса волны 32)
    if (context.w37RecruitHold && expectedReceived < unit.size) {
      const recruitHolderBonus = calcW37RecruitHolderBonus(
        context.w37RecruitHold.threatByQuarter.get(cellKey), unit, cellKey, context.w37RecruitHold.factor
      );
      if (recruitHolderBonus > cityHolderBonus) {
        score += recruitHolderBonus - cityHolderBonus;
        cityHolderBonus = recruitHolderBonus;
      }
    }
  } else {
    // Защита своего города: враг стоит на нашем квартале — идем к нему
    if (ownQuartersUnderAttack.size > 0) {
      const isDefendingPosition = ownQuarters.has(cellKey)
        || adjacentEnemies.some((enemy) => ownQuartersUnderAttack.has(enemy.hexKey));
      if (isDefendingPosition) {
        score += BOT_WEIGHTS.DEFEND_OWN_CITY;
      }
    }

    // Волна 25 (23.1): квартал под захватом СЛЕДУЮЩИМ ходом (враг смежно, свой
    // отряд на квартале отсутствует) — встать на него значит сорвать захват:
    // занятый владельцем квартал по правилам не захватывается. Живой тест 23:
    // демоны стояли у центра, пока противнику оставалось 1 перемещение до города
    if (context.imminentThreatQuarters?.has(cellKey)) {
      score += BOT_WEIGHTS.DEFEND_OWN_CITY;
    }
  }

  // Уклонение слабого (нюанс живого теста 12): при явном отставании армии
  // неагрессивный бот без боевой политики не подставляется под досягаемость
  // противника — кроме захвата и обороны самого ценного города под угрозой
  if (context.isOutmatchedDefender && !enemyQuarters.has(cellKey)) {
    const isDefendingTopCity = context.isTopCityThreatened
      && context.topOwnCityQuarters
      && (context.topOwnCityQuarters.has(cellKey)
        || getNeighborHexKeys(cellKey, context.gridRenderer).some(
          (adjKey) => context.topOwnCityQuarters.has(adjKey)
        ));
    // Волна 32 (оборона): квартал ЛЮБОГО своего города под риском захвата — не
    // «подставление», а оборона (у legacy cityThreatByQuarter = null)
    const isGuardingThreatenedCity = Boolean(context.cityThreatByQuarter)
      && (context.cityThreatByQuarter.get(cellKey)?.level || 0) > CITY_THREAT_LEVELS.NONE;
    if (!isDefendingTopCity && !isGuardingThreatenedCity) {
      const isInEnemyReach = units.some((otherUnit) =>
        otherUnit.ownerId !== unit.ownerId
        && otherUnit.ownerId !== NEUTRAL_PLAYER_ID
        && calcHexDistance(otherUnit.hexKey, cellKey) <= 2
      );
      if (isInEnemyReach) {
        score -= BOT_WEIGHTS.OUTMATCHED_ADVANCE_PENALTY;
      }
    }
  }

  // Крепости (нюансы живого теста 10): свободную достроенную занимаем с высоким
  // приоритетом; свою недостроенную защищаем при близком враге; недостроенную
  // крепость противника блокируем (смежный чужой отряд не дает ее достроить),
  // если намерены проявлять агрессию и позиция выживаема
  if (context.fortressCells && context.fortressCells.size > 0) {
    const fortressHere = context.fortressCells.get(cellKey);
    if (fortressHere) {
      const isOwnFortress = fortressHere.color === currentPlayer.color;
      if (fortressHere.isCompleted) {
        // Чужая крепость, своя при нападении на город, либо слабый защитник
        // (держим стены вместо вылазки на сильного) — двойная ценность
        const isDoubleValue = !isOwnFortress
          || ownQuartersUnderAttack.size > 0
          || context.isOutmatchedDefender;
        score += BOT_WEIGHTS.FORTRESS_OCCUPY_BONUS * (isDoubleValue ? 2 : 1);
      } else if (isOwnFortress) {
        // Волна 20 (19.8): радиус угрозы недостроенной крепости расширен до 4
        // ячеек (~1-2 хода) — раньше реагируем на подход противника, а не
        // ждем его вплотную
        const fortressThreatRadius = 4;
        const hasEnemyNear = units.some((otherUnit) =>
          otherUnit.ownerId !== unit.ownerId
          && otherUnit.ownerId !== NEUTRAL_PLAYER_ID
          && calcHexDistance(otherUnit.hexKey, cellKey) <= fortressThreatRadius
        );
        if (hasEnemyNear) {
          score += BOT_WEIGHTS.FORTRESS_DEFEND_UNFINISHED_BONUS;
        }
      }
    }

    const isAggressionIntent = Boolean(context.campaignByUnit?.get(unit.id)) || profile.military > 1;
    if (isAggressionIntent && expectedReceived < unit.size) {
      const isEnemyUnfinishedFortress = (fortress) => fortress
        && !fortress.isCompleted
        && fortress.color !== currentPlayer.color
        && fortress.color !== NEUTRAL_PLAYER_COLOR;
      const blocksEnemyFortress = isEnemyUnfinishedFortress(fortressHere)
        || getNeighborHexKeys(cellKey, context.gridRenderer).some(
          (adjKey) => isEnemyUnfinishedFortress(context.fortressCells.get(adjKey))
        );
      if (blocksEnemyFortress) {
        score += BOT_WEIGHTS.FORTRESS_BLOCK_ENEMY_BONUS;
      }
    }
  }

  // Рост на своей казарме — до ~60% максимума: снабженный отряд растет на +1
  // на любой ячейке, поэтому d6 выдвигается уже с размера 4, дорастая в пути.
  // Смежный лагерь удваивает прирост (+4 за фазу снабжения) — держим отряд
  // дольше (до ~80% максимума) и вдвое сильнее.
  // Эльфы исключены: их рост +1 в раунд НЕ зависит от ячейки (балансное
  // ограничение) — казарма им нужна свободной под наймы.
  // Крысолюды исключены: их рой растет на фермах.
  const unitConfig = getUnitTypeConfig(unit.type);
  // Волна 39: великану — цель роста фракции (40), а не 20 типа d20
  const maxSize = getW39GrowthMaxSize(unit, unitConfig?.maxSize || 0);
  if (hexes[cellKey] === COMMON_BUILDING_TOKENS.BARRACKS
    && unit.size < maxSize
    && ownQuarters.size > 0
    && unit.factionId !== FACTIONS.ELVES
    && unit.factionId !== FACTIONS.RATMEN
  ) {
    const isOwnBarracks = (context.citiesCache?.[currentPlayer.color] || []).some((city) => {
      const cityBarracks = city[COMMON_BUILDING_TOKENS.BARRACKS];
      return Array.isArray(cityBarracks) && cityBarracks.includes(cellKey);
    });
    const isCampAdjacent = isOwnBarracks && context.specialBuildings
      && getNeighborHexKeys(cellKey, context.gridRenderer).some(
        (adjKey) => context.specialBuildings[adjKey] === SPECIAL_BUILDING_TOKENS.MILITARY_CAMP
      );
    if (isOwnBarracks && context.w37CampHold && isCampAdjacent) {
      // Волна 37 (B2): на казарме у лагеря — по реальной разнице роста вместо
      // порога 80% и множителей ×2/×4: (рост на казарме − рост вне ее) × ценность
      // размера, пока разница > 0. Рост — по правилам редьюсера (рабочие казарма и
      // лагерь, фракция, прибавка лиги, предел размера), 0 без снабжения на клетке
      // и в последнем раунде (фазы снабжения после войны нет). Бонус до 40 —
      // перебивает марш кампании и градиент целей, но не держание при угрозе
      // этого раунда, убийство и захват. M4 снимает его, как прежний
      const isGrowthReleasedByThreat = context.strongCities
        && isBarracksGrowthReleased(context.bonusThreatReleasedUnitIds, unit.id);
      if (!isGrowthReleasedByThreat) {
        score += calcW37GrowthDeltaAt({
          unit,
          owner: currentPlayer,
          cellKey,
          barracksInfo: context.w37CampHold.barracksInfo,
          supplyCells: context.budgetAwareSupplyReachCells || supplyReachCells,
          isSupplyEverywhere: context.w37CampHold.isSupplyEverywhere,
          isFinalRound: context.w37CampHold.isFinalRound,
        }) * context.w37CampHold.growthPerSize;
      }
    } else if (isOwnBarracks) {
      let barracksGrowthCap = Math.ceil(maxSize * (isCampAdjacent ? 0.8 : 0.6));
      // Живой тест 16.3: в раундах 11–13 порог удержания на казарме тает —
      // невырощенный отряд должен успеть в бой до конца партии
      if (context.round >= FINAL_ROUND - 2) {
        barracksGrowthCap = Math.ceil(
          barracksGrowthCap * (FINAL_ROUND + 1 - context.round) / 4
        );
      }
      // Волна 33 (M4, только strongCities): отряд, снятый с роста из-за угрозы
      // фронтирному городу без гарнизона, бонуса роста не получает — иначе при
      // смежном лагере (×2) или у малого отряда (×4) казарма перебивала бы
      // гарнизонное удержание квартала (см. bonusThreatReleasedUnitIds)
      const isGrowthReleasedByThreat = context.strongCities
        && isBarracksGrowthReleased(context.bonusThreatReleasedUnitIds, unit.id);
      if (unit.size < barracksGrowthCap && !isGrowthReleasedByThreat) {
        // Малый отряд (размер <= 2) не должен покидать платформу роста —
        // уход "размером 1" к центру карты сжигает весь потенциал найма
        // (нюанс живого теста 11: демоны/зверолюды)
        const smallUnitHoldMultiplier = unit.size <= 2 ? 4 : 1;
        score += BOT_WEIGHTS.STAY_ON_BARRACKS_TO_GROW
          * (isCampAdjacent ? 2 : 1) * smallUnitHoldMultiplier;
      }
    }
  }

  // Эльфы: отряд на своей казарме при бюджете на найм блокирует платформу —
  // сходим заранее, чтобы в фазе войны место было свободно под новый отряд.
  // Волна 16 (живой тест 15.2): стоять на казарме после найма имеет смысл
  // только под докупку размера в следующем раунде (есть монеты и размер < 80%)
  if (unit.factionId === FACTIONS.ELVES
    && context.ownBarracksCells
    && context.ownBarracksCells.has(cellKey)
  ) {
    const maxSizeForVacate = getUnitTypeConfig(unit.type)?.maxSize || 0;
    const hasSizeBuyIntent = context.currentPlayer.coins >= 3
      && unit.size < maxSizeForVacate * 0.8;
    if (context.elfCanAffordHire || !hasSizeBuyIntent) {
      score -= BOT_WEIGHTS.ELF_BARRACKS_VACATE_PENALTY;
    }
  }

  // Эльфы: ближний бой оправдан только против БОЛЕЕ СЛАБЫХ отрядов (захват)
  // либо при обороне своего города — размер эльфов растет медленно, размен
  // даже при паритете невыгоден (живой тест 15.4)
  if (unit.factionId === FACTIONS.ELVES
    && adjacentEnemies.length > 0
    && !enemyQuarters.has(cellKey)
    && !ownQuarters.has(cellKey)
    && adjacentEnemies.some((enemy) => enemy.size >= unit.size)
  ) {
    score -= BOT_WEIGHTS.CAPTURE_ENEMY_QUARTER * 0.6;
  }

  // Волна 20 (19.3): эльфы категорически не заходят на ячейку, где смежные
  // отряды противника В СУММЕ равны или превышают размер отряда эльфов —
  // размер копится слишком медленно, чтобы жертвовать. Исключение: только
  // квартал СВОЕГО города, и только если превосходство противника меньше 3
  if (unit.factionId === FACTIONS.ELVES && adjacentEnemies.length > 0) {
    const adjacentEnemyTotalSize = adjacentEnemies.reduce((sum, enemy) => sum + enemy.size, 0);
    if (adjacentEnemyTotalSize >= unit.size) {
      const isOwnQuarterException = ownQuarters.has(cellKey)
        && (adjacentEnemyTotalSize - unit.size) < 3;
      if (!isOwnQuarterException) {
        score -= BOT_WEIGHTS.SUICIDE_PENALTY * 2;
      }
    }
  }

  // Оборона города: занимать КВАРТАЛ, а не просто вставать смежно противнику —
  // занятый квартал не может быть захвачен, даже если противник выживет
  // (живой тест 15.5, все фракции)
  if (ownQuarters.has(cellKey)
    && adjacentEnemies.length > 0
  ) {
    score += BOT_WEIGHTS.DEFEND_OWN_CITY * 0.5;
  }

  // Волна 33 (M4, только strongCities): гарнизон фронтирного города — единственный
  // свой отряд на кварталах города, размером ниже доли максимума (крупные идут
  // воевать), без кампании и форсированной цели, не на удержании роста на казарме.
  // Без угрозы в этом/следующем раунде движение иначе ничем не держит отряд в
  // городе: общий градиент, тяга к кузнице и штраф пустой ячейки уводят его, а
  // живой игрок забирает пустой город вместе с фермами. Надбавка та же на
  // «остаться» и на квартал того же города; ниже она же снимает тягу к кузнице
  let bonusGarrisonHold = 0;
  if (context.strongCities && context.bonusGarrisonByQuarter) {
    bonusGarrisonHold = calcFrontierGarrisonHold({
      unit,
      cellKey,
      garrisonByQuarter: context.bonusGarrisonByQuarter,
      maxSize,
      garrisonSizeShare: context.bonusProfile.garrisonSizeShare,
      hold: context.bonusProfile.frontierGarrisonHold,
      hasCampaign: Boolean(context.campaignByUnit?.has(unit.id)),
      isForcedCapture: Boolean(context.bonusForcedCaptureUnitIds?.has(unit.id)),
      isGrowthHeld: Boolean(context.bonusGrowthHoldUnitIds?.has(unit.id)),
    });
    score += bonusGarrisonHold;
  }

  // Волна 35 (G): гарнизон по доктрине — квартал своего города. Как и бонус
  // держателя волны 32 — только если отряд по матожиданию переживет собственный
  // ближний бой на этой клетке: мертвый гарнизон город не удержит (надбавка 100
  // перебивала штраф самоубийства 60 и вела гарнизон под удар).
  //  'hard': уйти с кварталов гарнизон не может (фильтр клеток в decideMovementStep),
  //    надбавка ведет назначенного отряда на свободный квартал, если он еще не на нем;
  //  'soft': фильтра нет — на квартале своего города гарнизон получает ценность
  //    держателя города (calcCityHolderValue) целиком, но не сверх бонуса держателя
  //    волны 32 на этой клетке. Уходит он, когда ход дает больше: захват решает
  //    обычная оценка
  // Волна 37 (B1): резервный держатель своего гарнизонного города держит его и с
  // казармы — та же надбавка на «остаться» (у legacy w37Reserve = null)
  const isW37GarrisonReserveCell = Boolean(garrisonCity)
    && checkIfW37ReserveForCity(w37Reserve, unit.id, garrisonCity.cityKey);
  if (garrisonCity && (garrisonCity.quarters.has(cellKey) || isW37GarrisonReserveCell)
    && expectedReceived < unit.size
  ) {
    score += context.w35GarrisonLockHard
      ? W35_GARRISON_HOLD_BONUS
      : Math.max(0, garrisonCity.holdValue - cityHolderBonus);
  }

  // Живой тест 16.6: крысиный d6 размера 4+ при готовности к агрессии не
  // отсиживается на кварталах своего города без противника рядом — ему место
  // на фронте (смежный союзник в атаке дает +2 урона фракционно)
  if (unit.factionId === FACTIONS.RATMEN
    && unit.size >= 4
    && context.isAggressionReady
    && ownQuarters.has(cellKey)
    && adjacentEnemies.length === 0
  ) {
    score -= BOT_WEIGHTS.RATMEN_FARM_BLOCKING_PENALTY;
  }

  // Гномы: позиция смежно горам ценнее при контакте с противником
  // (живой тест 15.5 — фракционный бонус гномов у гор)
  if (context.isDwarvesPlayer
    && adjacentEnemies.length > 0
    && getNeighborHexKeys(cellKey, context.gridRenderer)
      .some((adjKey) => hexes[adjKey] === TERRAIN_TOKENS.MOUNTAIN)
  ) {
    score += 8;
  }

  // Эльфы: кайтинг — дальность стрельбы 4 при скорости 1 у всех: позиция,
  // с которой можно стрелять (дистанция 2-4 до ближайшего врага), но нельзя
  // получить ближний бой, идеальна. Захват и оборона городов перевешивают.
  if (unit.factionId === FACTIONS.ELVES && adjacentEnemies.length === 0) {
    let nearestEnemyDistance = Infinity;
    let nearestEnemySize = 0;
    units.forEach((otherUnit) => {
      if (otherUnit.ownerId === unit.ownerId) {
        return;
      }
      // Волна 22 (21.6): нейтральные отряды НЕ перемещаются — кайтить от них
      // бессмысленно, дистанцию отхода определяют только отряды игроков
      if (otherUnit.ownerId === NEUTRAL_PLAYER_ID) {
        return;
      }
      const distance = calcHexDistance(otherUnit.hexKey, cellKey);
      if (distance < nearestEnemyDistance) {
        nearestEnemyDistance = distance;
        nearestEnemySize = otherUnit.size;
      }
    });
    if (nearestEnemyDistance >= 2 && nearestEnemyDistance <= ELVES_SHOOTING_RANGE) {
      // Волна 18 (живой тест 17.3): отходить на МАКСИМАЛЬНУЮ дистанцию стрельбы
      // (враг за ход скорости 1 не достанет), кроме случаев, когда выстрел по
      // матожиданию уничтожает цель (тогда добиваем вблизи) или отряд обороняет
      // свой город. Ожидаемый урон стрельбы эльфа ≈ половина ближнего боя.
      const expectedShotDamage = getExpectedMeleeDamage(unit.size) / 2;
      const isDefendingCity = ownQuarters.has(cellKey) || context.ownQuartersUnderAttack?.size > 0;
      if (!isDefendingCity && nearestEnemySize > expectedShotDamage) {
        score += BOT_WEIGHTS.ELF_KITE_BONUS
          + (nearestEnemyDistance - 2) * BOT_WEIGHTS.ELF_KITE_BONUS;
      } else {
        score += BOT_WEIGHTS.ELF_KITE_BONUS;
      }
    } else if (nearestEnemyDistance > ELVES_SHOOTING_RANGE
      && nearestEnemyDistance < Infinity
      && isPlayerPolicy(currentPlayer, POLICIES.PATRIOTISM)
    ) {
      // Волна 20 (19.15): эльфы с PATRIOTISM у 70%+ максимального размера
      // агрессивно сокращают дистанцию до дальности стрельбы вместо
      // пассивного ожидания — кайтинг (ветка выше) все еще держит их вне
      // ближнего боя, как только дистанция достигнута
      const unitMaxSizeForShooting = getUnitTypeConfig(unit.type)?.maxSize || 0;
      if (unitMaxSizeForShooting > 0 && unit.size >= unitMaxSizeForShooting * 0.7) {
        score += Math.max(0, 12 - nearestEnemyDistance) * (BOT_WEIGHTS.ELF_KITE_BONUS / 4);
      }
    }
  }

  // Сильный режим: прогноз ходов противников этого раунда
  if (context.predictedEnemyCellSizes) {
    // Не вставать под прогнозируемый удар превосходящих сил: сумма прогнозных
    // размеров чужих отрядов смежно позиции больше собственного размера — штраф.
    // Клетки захвата (чужие кварталы) не штрафуются — захват стоит риска, иначе
    // экономические фракции отказываются от захватов (отчет №10: просвещенные
    // -1.9 очков захвата в strong). Демоны: штраф вдвое мягче — их защита
    // это аура подавления урона (радиус 3 от отрядов противника)
    const isCaptureCell = enemyQuarters.has(cellKey);
    // Отчет №11: оборона своего города стоит риска — клетки своих кварталов и
    // смежные им не штрафуются прогнозом (просвещенные NS−N = −7.7: штраф
    // отгонял гарнизон от обороны накопленных очков в поздней игре)
    const isOwnCityDefenseCell = ownQuarters.has(cellKey)
      || getNeighborHexKeys(cellKey, context.gridRenderer).some((adjKey) => ownQuarters.has(adjKey));
    if (!isCaptureCell && !isOwnCityDefenseCell) {
      let predictedAdjacentSize = context.predictedEnemyCellSizes.get(cellKey) || 0;
      getNeighborHexKeys(cellKey, context.gridRenderer).forEach((adjKey) => {
        predictedAdjacentSize += context.predictedEnemyCellSizes.get(adjKey) || 0;
      });
      if (predictedAdjacentSize > unit.size) {
        const demonSoftening = context.isDemonsPlayer ? 0.5 : 1;
        // Волна 19 (отчет №13): штраф прогноза выгонял невоенные профили из
        // оспариваемого центра (контроль центральных точек: элементали
        // 1.35 -> 1.03, вентори 1.16 -> 0.96 в NS) — экономическим/защитным
        // профилям штраф вдвое мягче; бонусы обороны/окон захвата ниже — без
        // изменений (они и есть польза прогноза).
        // Волна 20 (диагностика отчета №14, SIM_STRONG_BLIND=1): у дворфов
        // "слепой" сильный режим (песочница считается, прогноз не
        // используется) практически СНЯЛ всю деградацию NS-N (было -8.4,
        // стало +0.2) — штраф прогноза действительно основной канал вреда,
        // прежнее смягчение x0.5 недостаточно, углубляем до x0.2.
        // Крысолюды/инсектоиды (military>1, старым смягчением не покрыты) по
        // отчету №14 тоже стабильно теряют в NS сильнее остальных
        // раш-фракций — распространяем облегченное смягчение x0.5 (по
        // аналогии с демонами) и на них
        const econProfileSoftening = context.profile.military <= 1
          ? 0.2
          : (context.isRatmenPlayer || context.isInsectoidsPlayer ? 0.5 : 1);
        score -= (predictedAdjacentSize - unit.size)
          * BOT_WEIGHTS.STRONG_PREDICTED_THREAT_FACTOR * demonSoftening * econProfileSoftening;
      }
    }
    // Свой квартал под прогнозируемым захватом: встать на него — сорвать захват
    if (context.predictedThreatenedOwnQuarters?.has(cellKey)) {
      score += BOT_WEIGHTS.DEFEND_OWN_CITY * BOT_WEIGHTS.STRONG_DEFEND_PREDICTED_FACTOR;
    }
    // Чужой квартал, который защита покинет по прогнозу — окно для захвата
    if (context.predictedVacatedEnemyQuarters?.has(cellKey)) {
      score += BOT_WEIGHTS.CAPTURE_ENEMY_QUARTER * BOT_WEIGHTS.STRONG_VACATED_CAPTURE_FACTOR
        * context.profile.captureEagerness;
    }
  }

  // Эльфы: выросший отряд тянется к свободной своей казарме под апгрейд
  // (d6 -> d10 -> d20 — единственный способ продолжить рост)
  if (context.elfUpgradeReturnUnits?.has(unit.id) && context.elfUpgradeReturnDistances) {
    const upgradeDistance = context.elfUpgradeReturnDistances.get(cellKey);
    if (upgradeDistance !== undefined) {
      score -= upgradeDistance * BOT_WEIGHTS.CAMPAIGN_APPROACH_PER_CELL;
    }
  }

  // Кампания отряда (многораундовая цель) — сильный направляющий градиент
  const campaign = context.campaignByUnit?.get(unit.id);
  if (campaign) {
    const campaignDistance = context.campaignDistances?.get(campaign.targetHexKey)?.get(cellKey);
    if (campaignDistance !== undefined) {
      score -= campaignDistance * BOT_WEIGHTS.CAMPAIGN_APPROACH_PER_CELL;
    }
  } else {
    // Продвижение к ближайшей общей цели (чужие кварталы, неконтролируемые ключевые точки)
    const distanceToTarget = targetDistances.get(cellKey);
    if (distanceToTarget !== undefined) {
      score -= distanceToTarget * BOT_WEIGHTS.APPROACH_TARGET_PER_CELL;
    }

    // Давление ботов: дополнительная, более слабая тяга к кварталам игрока под
    // давлением. Именно тяга поверх обычного градиента, а не подмена цели —
    // отряд с собственной кампанией (ветка выше) ее не чувствует вовсе.
    // Волна 35 (G): гарнизон — тоже (давление на лидера на него не действует)
    if (context.pressureTargetDistances && !garrisonCity) {
      const pressureDistance = context.pressureTargetDistances.get(cellKey);
      if (pressureDistance !== undefined) {
        score -= pressureDistance * BOT_WEIGHTS.APPROACH_TARGET_PER_CELL
          * BOT_PRESSURE_APPROACH_WEIGHT;
      }
    }

    // Волна 33 (M2, только strongCities): такая же слабая тяга к кварталам
    // ближайшего противника оси (BFS оси раунда) — множители давления выше
    // работают не только у отрядов, уже стоящих рядом с ним. Отдельный градиент,
    // а не подмена целей: общий градиент и давление рейтинга не меняются
    if (context.bonusTargetDistances && !garrisonCity) {
      const bonusTargetDistance = context.bonusTargetDistances.get(cellKey);
      if (bonusTargetDistance !== undefined) {
        score -= bonusTargetDistance * BOT_WEIGHTS.APPROACH_TARGET_PER_CELL
          * context.bonusProfile.targetEnemyApproachWeight;
      }
    }

    // Волна 20 (19.1/19.2): крупный отряд без кампании (Вентори и другие
    // простаивали в тыловом стартовом городе) — сильное тяготение к своему
    // прифронтовому городу (ближайшему к живому противнику). Size>=8 — любой
    // архетип; >=50% максимума — только экономические/агрессивные архетипы
    if (context.idleArmyFrontierDistances) {
      const unitMaxSizeForIdle = getUnitTypeConfig(unit.type)?.maxSize || 0;
      const isEconomicOrAggressiveProfile = profile.military > 1 || profile.economy >= 1.4;
      // Бонусный бот (см. configs/botBonusBehavior): к прифронтовому городу идут
      // и средние отряды, а не только крупные. Именно здесь армия богатого бота
      // и застревала — стройка уходила к противнику, а войска оставались в тылу
      const qualifiesForFrontierPull = unit.size >= 8
        || (isEconomicOrAggressiveProfile && unitMaxSizeForIdle > 0 && unit.size >= unitMaxSizeForIdle * 0.5)
        || (context.bonusProfile && unit.size >= BOT_BONUS_FRONTIER_PULL_MIN_UNIT_SIZE);
      // Волна 33 (M3, только strongCities): тяга (к кузнице) не трогает отряд на
      // удержании роста на казарме (у «великого» бота 12/ячейку перебивали
      // удержание 6, и отряды уходили недорощенными) и гарнизон фронтирного
      // города на этой ячейке (M4) — иначе удержание 10 проигрывало бы тяге
      // Волна 37 (B2, все боты «золото+»): и отряд на удержании роста на казарме у
      // лагеря (w37CampHoldUnitIds; у legacy и остальных ботов null)
      const isBonusPullExcluded = (context.strongCities
        && (Boolean(context.bonusGrowthHoldUnitIds?.has(unit.id)) || bonusGarrisonHold > 0))
        || Boolean(context.w37CampHoldUnitIds?.has(unit.id));
      // Волна 35 (G): гарнизон к фронту и к кузнице не тянется
      if (qualifiesForFrontierPull && !isBonusPullExcluded && !garrisonCity) {
        const frontierDistance = context.idleArmyFrontierDistances.get(cellKey);
        if (frontierDistance !== undefined) {
          score -= frontierDistance * BOT_WEIGHTS.IDLE_ARMY_FRONTIER_PER_CELL
            * (context.bonusProfile ? context.bonusProfile.frontierPullMultiplier : 1);
        }
      }
    }
  }

  // Волна 39: фракционная игра великанов (бой только от превосходства, молодые —
  // вне досягаемости врага), суккубов (позиция соблазнителя) и гоблинов (позиция
  // у строений противника). У legacy и прочих фракций контексты null — 0
  if (checkIfW39GiantUnit(unit) && unit.ownerId === currentPlayer.id) {
    score += scoreW39GiantPosition({
      unit,
      cellKey,
      units,
      adjacentEnemies,
      isOwnQuarter: ownQuarters.has(cellKey),
      isEnemyQuarter: enemyQuarters.has(cellKey),
      suicidePenalty: BOT_WEIGHTS.SUICIDE_PENALTY,
      isOwnBarracks: Boolean(context.ownBarracksCells?.has(cellKey)),
      isCampAdjacent: Boolean(context.specialBuildings) && getNeighborHexKeys(cellKey, context.gridRenderer).some(
        (adjKey) => context.specialBuildings[adjKey] === SPECIAL_BUILDING_TOKENS.MILITARY_CAMP
      ),
      ownFarmsCount: context.ownFarmCells ? context.ownFarmCells.size : 0,
      attackDistance: context.w40GiantAttackDistances?.get(cellKey),
    });
  }
  score += scoreW39SeducerPosition(unit, cellKey, units, context.w39SuccubesContext);
  score += scoreW39GoblinPosition(unit, cellKey, units, context.w39GoblinsContext);

  return score;
};

/**
 * Этап перемещения фазы войны: одно перемещение за тик.
 * Выбирается (отряд, ячейка) с максимальным приростом оценки относительно текущей позиции.
 */
export const decideMovementStep = (store) => {
  const state = store.getState();
  const currentPlayer = selectCurrentPlayer(state);
  const hexes = selectHexes(state);
  const units = selectUnits(state);
  const players = selectPlayers(state);
  // Ячейки, где на этом этапе кто-то подорвался на ловушке: вставать на них
  // нельзя до конца этапа (сквозь — можно), см. selectGoblinsTrapDeathHexKeys
  const trapDeathHexKeys = selectGoblinsTrapDeathHexKeys(state);
  const citiesCache = selectCitiesCache(state);
  const pendingMovementActions = selectPendingMovementActions(state);

  const gridRenderer = getBotGridRenderer(state);
  const distanceHexes = getBotMovementDistanceHexes(currentPlayer, hexes);
  // Кража монет — тоже действие отряда в фазе войны: укравший лепреконский
  // отряд не может еще и переместиться (живой тест 16.9 — нарушение правил,
  // фикс вне A/B-гейтов; обратный порядок закрыт фильтром по перемещениям)
  const stealActionsForUsedUnits = selectLeprechaunsStealCoinsActions(state);
  const playerUnits = getPlayerUnits(currentPlayer, units)
    .filter((unit) => !pendingMovementActions.some((action) => action.unitId === unit.id)
      && !stealActionsForUsedUnits.some((action) => action.unitId === unit.id));

  if (playerUnits.length === 0) {
    return false;
  }

  const enemyQuarters = getEnemyQuarterHexKeys(citiesCache, currentPlayer.color);
  const ownQuarters = getOwnQuarterHexKeys(citiesCache, currentPlayer.color);
  const controlledKeyPoints = getControlledKeyPointHexKeys(citiesCache);

  // Волна 22 (21.12): кварталы чужих/нейтральных городов С ФЕРМОЙ — захват
  // такого города априори даст снабжение захватившему отряду
  const farmCityQuarters = new Set();
  Object.entries(citiesCache || {}).forEach(([cityColor, colorCities]) => {
    if (cityColor === currentPlayer.color) {
      return;
    }
    (colorCities || []).forEach((city) => {
      if ((city[COMMON_BUILDING_TOKENS.FARM] || []).length > 0) {
        getAllQuartersOfCity(city).forEach((quarterHexKey) => farmCityQuarters.add(quarterHexKey));
      }
    });
  });

  // Волна 22 (21.3): неконтролируемые ключевые точки центральной зоны — демоны
  // обязаны их оспаривать (позиции на/рядом получают бонус в скоринге)
  let uncontrolledCentralKeyPoints = null;
  if (isPlayerFaction(currentPlayer, FACTIONS.DEMONS)) {
    uncontrolledCentralKeyPoints = new Set();
    Object.entries(hexes).forEach(([hexKey, token]) => {
      if (checkIfKeyPointToken(token)
        && !controlledKeyPoints.has(hexKey)
        && calcHexDistance(hexKey, '0,0') <= 4
      ) {
        uncontrolledCentralKeyPoints.add(hexKey);
      }
    });
  }

  // Волна 22 (21.9b): вентори/просвещенные до 11 раунда не стремятся захватывать
  // нейтральные города, к которым уже есть путь влияния от своей гильдии/храма —
  // захват снизит доход влияния и потреплет отряд об охрану города
  let influenceConnectedNeutralQuarters = null;
  if ((isPlayerFaction(currentPlayer, FACTIONS.VENTORY)
      || isPlayerFaction(currentPlayer, FACTIONS.ENLIGHTENED))
    && selectRound(state) < 11
  ) {
    const ownInfluenceBuildings = getOwnInfluenceBuildings(
      selectSpecialBuildings(state), currentPlayer.color, citiesCache
    );
    if (ownInfluenceBuildings.length > 0) {
      const connectedQuarters = new Set();
      (citiesCache?.[NEUTRAL_PLAYER_COLOR] || []).forEach((city) => {
        const hasInfluencePath = ownInfluenceBuildings.some((building) =>
          projectInfluenceBuildingIncome(
            building.token, building.hexKey, currentPlayer, [city], hexes
          ) > 0);
        if (hasInfluencePath) {
          getAllQuartersOfCity(city).forEach((quarterHexKey) => connectedQuarters.add(quarterHexKey));
        }
      });
      if (connectedQuarters.size > 0) {
        influenceConnectedNeutralQuarters = connectedQuarters;
      }
    }
  }

  // Кварталы своих городов, занятые противником (угроза захвата)
  const ownQuartersUnderAttack = new Set();
  units.forEach((unit) => {
    if (unit.ownerId !== currentPlayer.id && ownQuarters.has(unit.hexKey)) {
      ownQuartersUnderAttack.add(unit.hexKey);
    }
  });

  // Волна 25 (23.1, запечена): свои кварталы под захватом следующим ходом —
  // отряд ЖИВОГО противника смежно кварталу, а своего отряда на квартале нет
  // (нейтралы статичны — не угроза). Занять такой квартал = сорвать захват
  let imminentThreatQuarters = new Set();
  ownQuarters.forEach((city, quarterHexKey) => {
    const isOccupiedByOwn = units.some((unit) =>
      unit.ownerId === currentPlayer.id && unit.hexKey === quarterHexKey);
    if (isOccupiedByOwn) {
      return;
    }
    const hasAdjacentLiveEnemy = units.some((unit) =>
      unit.ownerId !== currentPlayer.id
      && unit.ownerId !== NEUTRAL_PLAYER_ID
      && calcHexDistance(unit.hexKey, quarterHexKey) <= 1);
    if (hasAdjacentLiveEnemy) {
      imminentThreatQuarters.add(quarterHexKey);
    }
  });
  if (imminentThreatQuarters.size === 0) {
    imminentThreatQuarters = null;
  }

  // Волна 32 (оборона, гейт isPreW32DefensePlayer): риск захвата по ГОРОДАМ —
  // досягаемость живых противников и порядок хода в фазе войны (кэш досягаемости
  // на фазу). У legacy null: работают прежние imminentThreatQuarters и ownQuartersUnderAttack
  let cityThreatByQuarter = null;
  let w32CityThreats = null;
  let attackedCityQuarters = null;
  // Волна 35 (D1, гейт isPreW35DefenseSuicidePlayer): захват отрядом, который по
  // матожиданию погибнет в собственном бою, не засчитывается. Опирается на модель
  // угрозы волны 32: без нее (legacy обороны) не действует
  const isW32DefenseActive = !isPreW32DefensePlayer(currentPlayer.id);
  const isW35SuicideActive = isW32DefenseActive && !isPreW35DefenseSuicidePlayer(currentPlayer.id);
  if (isW32DefenseActive && ownQuarters.size > 0) {
    const cityCaptureAssessment = getCityCaptureThreats(state, currentPlayer);
    w32CityThreats = cityCaptureAssessment.cityThreats;
    cityThreatByQuarter = cityCaptureAssessment.threatByQuarter;
    // Бонус «атакованного города» (+30) — только кварталам того города, где враг уже стоит на квартале
    attackedCityQuarters = new Set();
    ownQuartersUnderAttack.forEach((quarterHexKey) => {
      getAllQuartersOfCity(ownQuarters.get(quarterHexKey))
        .forEach((cityQuarterHexKey) => attackedCityQuarters.add(cityQuarterHexKey));
    });
  }

  // Волна 37 (только боты «золото+», checkIfW36Feature; ручки — configs/botW37Barracks):
  //  B1 (гейт isPreW37BarracksStayPlayer) — без угрозы этого раунда отряд на своей
  //    казарме не уходит на кварталы ее города (резервный держатель, фильтр клеток);
  //  B1 или T (isPreW37ThreatHirePlayer) — держатель квартала города, которому в
  //    этом раунде угрожает только фантомный найм ходящего после бота;
  //  B2 (isPreW37CampHoldPlayer) — стояние на казарме у лагеря по разнице роста.
  // B1 и фантом опираются на модель угрозы волны 32 (без нее не действуют). Угроза
  // этого раунда — свой вызов с фантомным наймом (getW37ThisRoundCityThreats). У
  // остальных ботов и legacy-наборов — ни одного нового расчета
  const gameTypeForW37 = selectGameType(state);
  const isW37BarracksStayActive = w32CityThreats !== null
    && checkIfW36Feature(currentPlayer, gameTypeForW37, isPreW37BarracksStayPlayer);
  const isW37RecruitHoldActive = w32CityThreats !== null && (isW37BarracksStayActive
    || checkIfW36Feature(currentPlayer, gameTypeForW37, isPreW37ThreatHirePlayer));
  const isW37CampHoldActive = ownQuarters.size > 0
    && checkIfW36Feature(currentPlayer, gameTypeForW37, isPreW37CampHoldPlayer);
  const w37Knobs = isW37RecruitHoldActive || isW37CampHoldActive ? getW37BarracksKnobs() : null;
  const w37ThisRound = isW37RecruitHoldActive
    ? getW37ThisRoundCityThreats(state, currentPlayer, {humanRecruitsOnly: w37Knobs.isHumanRecruitsOnly})
    : null;

  // Волна 25 (23.8): «перезаписанные» захваты — незащищенный (без нейтральной
  // охраны на/рядом с кварталами) и не находящийся под угрозой перезахвата
  // (без отрядов живых противников в 2 ячейках) чужой город, на квартале
  // которого УЖЕ стоит свой отряд: остальные кварталы такого города не должны
  // притягивать дополнительные отряды — одного любого размера достаточно.
  // Занятый своим отрядом квартал в набор не входит (его удерживающий отряд
  // сохраняет бонус захвата в скоринге позиции)
  let oversubscribedCaptureQuarters = null;
  let oversubscribedCampaignQuarters = null;
  {
    const cityQuarterGroups = new Map();
    enemyQuarters.forEach((cityInfo, quarterHexKey) => {
      if (!cityQuarterGroups.has(cityInfo.city)) {
        cityQuarterGroups.set(cityInfo.city, []);
      }
      cityQuarterGroups.get(cityInfo.city).push(quarterHexKey);
    });
    oversubscribedCaptureQuarters = new Set();
    oversubscribedCampaignQuarters = new Set();
    cityQuarterGroups.forEach((quarterHexKeys) => {
      const hasOwnUnitOnQuarter = quarterHexKeys.some((quarterHexKey) =>
        units.some((unit) => unit.ownerId === currentPlayer.id && unit.hexKey === quarterHexKey));
      if (!hasOwnUnitOnQuarter) {
        return;
      }
      const isDefendedOrContested = units.some((unit) => {
        if (unit.ownerId === currentPlayer.id) {
          return false;
        }
        // Нейтральный охранник на/рядом с кварталом защищает город; отряд
        // живого противника в 2 ячейках — угроза перезахвата/уничтожения
        const threatRadius = unit.ownerId === NEUTRAL_PLAYER_ID ? 1 : 2;
        return quarterHexKeys.some(
          (quarterHexKey) => calcHexDistance(unit.hexKey, quarterHexKey) <= threatRadius
        );
      });
      if (isDefendedOrContested) {
        return;
      }
      quarterHexKeys.forEach((quarterHexKey) => {
        // Кампании отменяются на ВЕСЬ город (включая занятый квартал — его
        // удерживает бонус захвата в скоринге позиции, кампания не нужна);
        // из скоринга/градиента исключаются только НЕзанятые кварталы
        oversubscribedCampaignQuarters.add(quarterHexKey);
        const isOccupiedByOwn = units.some((unit) =>
          unit.ownerId === currentPlayer.id && unit.hexKey === quarterHexKey);
        if (!isOccupiedByOwn) {
          oversubscribedCaptureQuarters.add(quarterHexKey);
        }
      });
    });
    if (oversubscribedCaptureQuarters.size === 0) {
      oversubscribedCaptureQuarters = null;
    }
    if (oversubscribedCampaignQuarters.size === 0) {
      oversubscribedCampaignQuarters = null;
    }
  }

  // Профиль архетипа: агрессивность, накопление армии перед атакой, радиус экспансии
  const strategy = getBotStrategy(currentPlayer.id);
  const profile = getBotProfile(state, currentPlayer);
  // Волна 35 (G, checkIfW35GarrisonPlayer: бонус «большой» и выше, гейт
  // isPreW35GarrisonPlayer): гарнизоны по доктрине фракции — назначение раз в ход
  // войны (strategy.w35Garrisons, evaluation/garrisons): отряд → кварталы своего
  // города. Гарнизон держит кварталы (в режиме 'hard' — только по ним и ходит, в
  // 'soft' — получает на них ценность держателя), не берет кампании и
  // форсированные марши, не тянется к фронту, давление на лидера на него не
  // действует. У прочих ботов и legacy null
  const w35GarrisonByUnitId = checkIfW35GarrisonPlayer(currentPlayer, selectGameType(state))
    ? buildGarrisonUnitIndex(ensureGarrisonAssignments(state, currentPlayer))
    : null;
  const isW35Garrison = (unitId) => Boolean(w35GarrisonByUnitId && w35GarrisonByUnitId.has(unitId));
  const isW35GarrisonLockHard = Boolean(w35GarrisonByUnitId) && isGarrisonLockHard();
  // Волна 35 (K, гейт isPreW35CampaignRangePlayer): дальность и застревание захватных кампаний
  const w35CampaignRangeActive = !isPreW35CampaignRangePlayer(currentPlayer.id);
  // Волна 32 (логистика): подчасти перемещения — новое поведение, если игрок не в
  // legacy-наборе подчасти и не в зонтичном наборе логистики. L3 — марш через разрыв
  // снабжения, L4 — контроль прогресса кампаний, L5 — порядок захватных целей
  const w32MarchActive = !isPreW32MarchPlayer(currentPlayer.id);
  const w32StallActive = !isPreW32StallPlayer(currentPlayer.id);
  const w32TargetOrderActive = !isPreW32TargetOrderPlayer(currentPlayer.id);
  const bonusProfile = getBotBonusProfile(currentPlayer, selectGameType(state));
  // Волна 33 («сильные города», см. configs/botBonusBehavior): пакет включен только у
  // LARGE/GREAT вне legacy-группы A/B и только при оси удара текущего раунда
  // (ensureBonusAxis — кэш на раунд, тот же вызов, что в стройке: ось не может
  // остаться прошлораундной, если шаг вызван минуя executeBotStep; null — нет
  // своих городов или живых противников). Все новые ветки ниже — строго под
  // strongCities && bonusAxis; без них код выполняется дословно как раньше
  const strongCities = Boolean(bonusProfile?.strongCities);
  const bonusAxis = strongCities ? ensureBonusAxis(state, currentPlayer, bonusProfile) : null;
  const isBonusArmyPhaseNow = strongCities && bonusAxis && isBonusArmyPhase(selectRound(state));
  // Волна 33 (M4): гарнизоны фронтирных городов — квартал → город, его казармы и
  // свои отряды на его кварталах (текущие позиции, пересчет каждый тик)
  const bonusGarrisonByQuarter = strongCities && bonusAxis
    ? buildFrontierGarrisonIndex({
      ownCities: citiesCache?.[currentPlayer.color] || [],
      axis: bonusAxis,
      units,
      currentPlayerId: currentPlayer.id,
    })
    : null;
  const ownArmyStrength = calcPlayerArmyStrength(currentPlayer.id, units);
  let isAggressionReady = ownArmyStrength >= profile.buildupArmySize;

  // Уклонение слабого (нюанс живого теста 12): значительно уступающий по армии
  // неагрессивный бот без боевой политики не лезет в зону досягаемости противника —
  // исключения: захваты и оборона самого ценного своего города под угрозой
  const strongestOpponentTotal = calcStrongestOpponentArmy(currentPlayer.id, players, units);
  const hasMeleeBonusPolicy = isPlayerPolicy(currentPlayer, POLICIES.BARBARISM)
    || isPlayerPolicy(currentPlayer, POLICIES.MILITARISM);
  const isOutmatchedDefender = ownArmyStrength < strongestOpponentTotal * 0.75
    && profile.military <= 1
    && !hasMeleeBonusPolicy;

  let topOwnCityQuarters = null;
  let isTopCityThreatened = false;
  if (isOutmatchedDefender) {
    let bestCity = null;
    let bestKeyPoints = -1;
    (citiesCache?.[currentPlayer.color] || []).forEach((city) => {
      const keyPointsCount = (city.mine || []).length + (city.super_mine || []).length
        + (city.ruins || []).length + (city.super_ruins || []).length;
      if (keyPointsCount > bestKeyPoints) {
        bestKeyPoints = keyPointsCount;
        bestCity = city;
      }
    });
    if (bestCity) {
      topOwnCityQuarters = new Set(getAllQuartersOfCity(bestCity));
      isTopCityThreatened = units.some((unit) =>
        unit.ownerId !== currentPlayer.id
        && unit.ownerId !== NEUTRAL_PLAYER_ID
        && [...topOwnCityQuarters].some((quarterHexKey) => calcHexDistance(unit.hexKey, quarterHexKey) <= 3)
      );
    }
  }

  // Инсектоиды: копим 2+ отряда до более чем половины лимита размера на казармах,
  // затем давим числом ближайшие города противника
  const isInsectoidsPlayer = isPlayerFaction(currentPlayer, FACTIONS.INSECTOIDS);
  const isRatmenPlayer = isPlayerFaction(currentPlayer, FACTIONS.RATMEN);
  const isDemonsPlayer = isPlayerFaction(currentPlayer, FACTIONS.DEMONS);
  if (isInsectoidsPlayer) {
    const grownUnits = getPlayerUnits(currentPlayer, units).filter(
      (unit) => unit.size > (getUnitTypeConfig(unit.type)?.maxSize || 0) / 2
    );
    isAggressionReady = grownUnits.length >= 2;
  }

  // Крысолюды: волновые удары — с 3 раунда набеги на нейтральные и вражеские
  // города идут независимо от паритета армий (потерянные d6 бесплатно
  // восполняются наймом за снабжение), до этого — при сопоставимой армии
  if (isRatmenPlayer) {
    const strongestOpponent = calcStrongestOpponentArmy(currentPlayer.id, players, units);
    isAggressionReady = selectRound(state) >= 3 || ownArmyStrength >= strongestOpponent * 0.9;
  }

  // Демоны: первые 3 раунда — рудники стройкой и рост, города с 4 раунда
  // накопившими размер отрядами (порог армии снижен по итогам отчета №6:
  // ожидание 10 размера опаздывало против раш-меты)
  if (isDemonsPlayer) {
    isAggressionReady = selectRound(state) >= 4 && ownArmyStrength >= 8;
  }

  // Зверолюды: выросшая армия обязана воевать — при паритете (90% сильнейшего
  // противника) переходим к захватам, не дожидаясь профильных триггеров
  if (isPlayerFaction(currentPlayer, FACTIONS.BEASTMEN)) {
    const strongestForBeastmen = calcStrongestOpponentArmy(currentPlayer.id, players, units);
    if (ownArmyStrength >= strongestForBeastmen * 0.9) {
      isAggressionReady = true;
    }
  }

  // Люди: купленная боевая политика при паритете армии обязана конвертироваться
  // в атаку — ситуативный буст без боя выброшен впустую (живой тест 13)
  if (isPlayerFaction(currentPlayer, FACTIONS.HUMANS)
    && hasMeleeBonusPolicy
    && ownArmyStrength >= strongestOpponentTotal * 0.9
  ) {
    isAggressionReady = true;
  }

  // Лепреконы: накопленные d20 обязаны конвертироваться в захваты — поздняя
  // агрессия при суммарном размере d20 >= 13 и более слабом соседе (живой тест 14)
  const isLeprechaunsPlayer = isPlayerFaction(currentPlayer, FACTIONS.LEPRECHAUNS);
  if (isLeprechaunsPlayer && selectRound(state) >= 9) {
    const d20TotalSize = getPlayerUnits(currentPlayer, units)
      .filter((unit) => unit.type === UNIT_TYPES.D20.id)
      .reduce((sum, unit) => sum + unit.size, 0);
    if (d20TotalSize >= 13) {
      const hasWeakerOpponent = players.some((player) =>
        player.id !== currentPlayer.id
        && player.id !== NEUTRAL_PLAYER_ID
        && calcPlayerArmyStrength(player.id, units) < ownArmyStrength
      );
      if (hasWeakerOpponent) {
        isAggressionReady = true;
      }
    }
  }

  // Волна 17 (живой тест 16.3): с 10-го раунда пассивность запрещена — при
  // слабозащищенном городе в пределах 6 ячеек (защита у центра меньше силы
  // отрядов бота рядом) агрессия включается у всех фракций.
  // Лепреконы (16.9) — уже с 9-го раунда: их поздние d10/d20 успевают дойти
  if (!isAggressionReady) {
    const lateAggressionFromRound = isLeprechaunsPlayer ? 9 : 10;
    if (selectRound(state) >= lateAggressionFromRound
      && findLateAggressionTargets(currentPlayer, units, citiesCache, players, 6).length > 0
    ) {
      isAggressionReady = true;
    }
  }

  // Волна 33 (M1, только strongCities): в армейской фазе (первые 2/3 партии)
  // бонусный бот давит ближайшего противника независимо от накопления армии
  // архетипа — BUILDUP/ECON ждали 18-22 суммарного размера, DEFENSIVE не
  // атаковал до поздней агрессии вовсе. После фазы — штатные пороги. Штатная
  // готовность (без M1) запоминается для казарменной выдержки лепреконов ниже:
  // их d10/d20 < 40% максимума не должны уходить с казарм с первого раунда.
  // Поздняя агрессия выше только включает готовность, поэтому порядок вставки
  // (после нее) результата isAggressionReady не меняет
  const isAggressionReadyByRules = isAggressionReady;
  if (isBonusArmyPhaseNow) {
    isAggressionReady = true;
  }

  // Фокус экспансии (инсектоиды и крысолюды): рой/волна бьет ОДНУ цель вместе,
  // а не расползается по карте. Ближайший нейтральный город -> ближайший город
  // неагрессивной фракции, чья армия не доминирует -> без фокуса (центр + оборона).
  let cityFocusQuarters = null;
  if ((isInsectoidsPlayer || isRatmenPlayer) && isAggressionReady && enemyQuarters.size > 0) {
    const ownUnitCells = getPlayerUnits(currentPlayer, units).map((unit) => unit.hexKey);
    if (ownUnitCells.length > 0) {
      const fromUnitsDistances = calcBfsDistances(ownUnitCells, distanceHexes, gridRenderer);

      // Крепости не осаждаем: фокус только на города, чей гарнизон рой перебьет
      // (по данным симуляций инсектоиды на больших картах вязнут у сильных целей)
      const checkedGarrisons = new Map();
      const isCityTakeable = (cityInfo) => {
        if (!checkedGarrisons.has(cityInfo.city)) {
          const owner = players.find((player) => player.color === cityInfo.color);
          const garrison = calcCityGarrisonStrength(
            cityInfo.city, owner ? owner.id : null, units, gridRenderer
          );
          checkedGarrisons.set(cityInfo.city, garrison);
        }
        return checkedGarrisons.get(cityInfo.city) <= ownArmyStrength;
      };

      const pickNearestCity = (predicate) => {
        let best = null;
        enemyQuarters.forEach((cityInfo, quarterHexKey) => {
          if (!predicate(cityInfo) || !isCityTakeable(cityInfo)) {
            return;
          }
          const distance = fromUnitsDistances.get(quarterHexKey);
          if (distance !== undefined && (!best || distance < best.distance)) {
            best = {distance, city: cityInfo.city, color: cityInfo.color};
          }
        });
        return best;
      };

      // 1) ближайший нейтральный город. Вторая половина игры (живой тест 15.10):
      // нейтралы — цель только если досягаемы за одно перемещение (дистанция <= 3),
      // иначе агрессия направляется на противников.
      // Волна 23 (22.5): инсектоидам фокус на нейтральных городах ЗАПРЕЩЕН
      // категорически — рой вязнет у нейтралов и стоит на пустых ячейках;
      // цели: города живых противников (приоритетно неагрессивных) или центр
      const isSecondHalf = selectRound(state) > FINAL_ROUND / 2;
      const isInsectoidsNeutralFocusBanned = isInsectoidsPlayer;
      let focus = null;
      if (!isInsectoidsNeutralFocusBanned) {
        focus = pickNearestCity((cityInfo) => cityInfo.color === 'neutral');
        if (isSecondHalf && focus && focus.distance > 3) {
          focus = null;
        }
      }

      // 1b) Волна 33 (M8, только strongCities): в армейской фазе взятый город
      // ближайшего противника оси предпочтительнее нейтрала, если он не дальше
      // его больше чем на запас ячеек (у инсектоидов нейтрала нет — он же).
      // Единственное давление на enemyColor у роя: остальной пакет для
      // крысолюдов/инсектоидов сознательно не действует
      if (isBonusArmyPhaseNow) {
        focus = pickSwarmFocus({
          neutralFocus: focus,
          enemyColorFocus: pickNearestCity((cityInfo) => cityInfo.color === bonusAxis.enemyColor),
          extraDistance: BOT_BONUS_SWARM_FOCUS_ENEMY_EXTRA_DISTANCE,
        });
      }

      // 2) иначе — город неагрессивной фракции, не доминирующей по армии
      if (!focus) {
        focus = pickNearestCity((cityInfo) => {
          const owner = players.find((player) => player.color === cityInfo.color);
          if (!owner) {
            return false;
          }
          const primaryArchetype = getFactionPreferences(owner.factionId).archetypes[0] || '';
          if (primaryArchetype.startsWith('AGGRESSIVE')) {
            return false;
          }
          return calcPlayerArmyStrength(owner.id, units) <= ownArmyStrength;
        });
      }

      // 2b) Волна 23 (22.5): инсектоиды без неагрессивной цели атакуют ЛЮБОЙ
      // город живого противника, чей гарнизон рой перебьет (isCityTakeable)
      if (!focus && isInsectoidsNeutralFocusBanned) {
        focus = pickNearestCity((cityInfo) => cityInfo.color !== 'neutral');
      }

      if (focus) {
        cityFocusQuarters = new Set();
        enemyQuarters.forEach((cityInfo, quarterHexKey) => {
          if (cityInfo.city === focus.city) {
            cityFocusQuarters.add(quarterHexKey);
          }
        });
      }
    }
  }

  // Демоны в ранней игре не лезут на города агрессивных фракций (крысолюды,
  // инсектоиды, орки, другие демоны): их армия там перемелется без выгоды —
  // цели ранней агрессии: нейтральные города, слабые фракции, центр
  // (данные отчета №5: винрейт демонов 18.3% с агрессорами против 31.6% без)
  let demonsExcludedQuarters = null;
  if (isDemonsPlayer && selectRound(state) <= 7) {
    demonsExcludedQuarters = new Set();
    enemyQuarters.forEach((cityInfo, quarterHexKey) => {
      if (cityInfo.color === NEUTRAL_PLAYER_COLOR) {
        return;
      }
      const owner = players.find((player) => player.color === cityInfo.color);
      if (owner && DEMONS_EARLY_AVOID_FACTIONS.has(owner.factionId)) {
        demonsExcludedQuarters.add(quarterHexKey);
      }
    });
  }

  // Противники под давлением ботов: их цели получают множитель к оценке
  // (см. getPressureFactor). Признак взводит редьюсер на старте раунда
  // (applyBotPressureLatches) и только в рейтинговых партиях — в остальных
  // режимах оба набора пусты и ни один вес не меняется
  const pressuredEnemies = players.filter(
    (player) => player.isUnderBotFocus && player.id !== currentPlayer.id
  );
  const pressuredEnemyColors = new Set(pressuredEnemies.map((player) => player.color));
  // Волна 33 (M2): карты «ключ → множитель» вместо булевых наборов. У обычного
  // бота (без оси) в картах ровно игроки под фокусом с BOT_PRESSURE_TARGET_WEIGHT —
  // прежние 1.6 / 1; у бонусного бота ближайший противник оси добавляется со
  // своим весом targetEnemyWeight (при пересечении — максимум)
  const {byPlayerId: pressureFactorByPlayerId, byColor: pressureFactorByColor} = buildPressureFactorMaps({
    pressuredPlayers: pressuredEnemies,
    pressureWeight: BOT_PRESSURE_TARGET_WEIGHT,
    axis: strongCities ? bonusAxis : null,
    targetEnemyWeight: strongCities && bonusAxis ? bonusProfile.targetEnemyWeight : 1,
  });

  // Блокирующие позиции у ключевых точек противника: ячейка точки и смежные с ней,
  // ценность = доход точки за раунд (лучшая из блокируемых с позиции)
  const enemyKeyPointBlockValues = new Map();
  // Из них те, что душат экономику игрока под давлением: ячейка → множитель
  // (наибольший среди владельцев блокируемых с нее активов)
  const pressureFactorByCell = new Map();
  const registerBlockValue = (assetHexKey, value, ownerPressureFactor) => {
    [assetHexKey, ...getNeighborHexKeys(assetHexKey, gridRenderer)].forEach((cellKey) => {
      if (value > (enemyKeyPointBlockValues.get(cellKey) || 0)) {
        enemyKeyPointBlockValues.set(cellKey, value);
      }
      registerPressureFactor(pressureFactorByCell, cellKey, ownerPressureFactor);
    });
  };
  const keyPointOwnerColors = getEnemyKeyPointOwnerColors(citiesCache, currentPlayer.color);
  getEnemyControlledKeyPoints(citiesCache, currentPlayer.color).forEach((token, keyPointHexKey) => {
    const keyPointValue = getKeyPointValuePerRound(token);
    if (keyPointValue > 0) {
      registerBlockValue(
        keyPointHexKey, keyPointValue, getPressureFactor(pressureFactorByColor, keyPointOwnerColors.get(keyPointHexKey))
      );
    }
  });
  // Волна 16 (живой тест 15.9): блокировка ферм и спецстроений противника тоже
  // ценна — блокированная ферма не кормит, строение не работает
  Object.entries(citiesCache || {}).forEach(([cityColor, colorCities]) => {
    if (cityColor === currentPlayer.color || cityColor === NEUTRAL_PLAYER_COLOR) {
      return;
    }
    const ownerPressureFactor = getPressureFactor(pressureFactorByColor, cityColor);
    (colorCities || []).forEach((city) => {
      (city[COMMON_BUILDING_TOKENS.FARM] || []).forEach(
        (cellKey) => registerBlockValue(cellKey, 0.75, ownerPressureFactor)
      );
      getAllQuartersOfCity(city).forEach((quarterHexKey) => {
        if (selectSpecialBuildings(state)[quarterHexKey]) {
          registerBlockValue(quarterHexKey, 1, ownerPressureFactor);
        }
      });
    });
  });

  // Сильный режим: прогноз ходов следующих противников раунда (если посчитан)
  const predictions = getBotPredictions(currentPlayer.id, selectRound(state));

  // Демоны: карты дальности (радиус ауры) от каждого отряда противника — считаются
  // один раз на тик, скоринг позиций проверяет попадание ячейки в радиус.
  // Волна 16: в сильном режиме аура строится по ПРОГНОЗНЫМ позициям противника —
  // прогноз работает НА ауру (встать туда, где противник окажется в радиусе 3),
  // а не против нее (отчет №10: демоны -6.7 в strong)
  let demonAuraMaps = null;
  if (isDemonsPlayer) {
    // Волна 17 (отчет №11, NS−N = −5.3): прогнозной ауре можно верить только
    // при паритете сил — более сильный противник не обязан ходить как в
    // прогнозе, и вставший «под прогноз» демон ловит реальный удар
    const canTrustAuraPredictions = ownArmyStrength >= strongestOpponentTotal * 0.8;
    const auraSourceCells = predictions?.predictedUnits?.length && canTrustAuraPredictions
      ? predictions.predictedUnits.map((unit) => unit.hexKey)
      : units.filter((unit) => unit.ownerId !== currentPlayer.id).map((unit) => unit.hexKey);
    demonAuraMaps = auraSourceCells
      .map((cellKey) => calcBfsDistances([cellKey], distanceHexes, gridRenderer, DEMONS_ABILITY_RANGE));
  }

  const homeDistances = profile.expansionRadius && strategy.homeHexKey
    ? calcBfsDistances([strategy.homeHexKey], distanceHexes, gridRenderer)
    : null;

  // Вторая половина игры: агрессивные фракции не тянутся к дальним нейтральным
  // городам (живой тест 15.10) — дистанции от своих отрядов для проверки
  const isSecondHalfForTargets = selectRound(state) > FINAL_ROUND / 2;
  const isAggressiveFactionForNeutrals = isInsectoidsPlayer || isRatmenPlayer || isDemonsPlayer
    || isPlayerFaction(currentPlayer, FACTIONS.ORCS);
  let ownUnitDistancesForTargets = null;
  if (isSecondHalfForTargets && isAggressiveFactionForNeutrals) {
    const ownUnitCellsForTargets = getPlayerUnits(currentPlayer, units).map((unit) => unit.hexKey);
    if (ownUnitCellsForTargets.length > 0) {
      ownUnitDistancesForTargets = calcBfsDistances(ownUnitCellsForTargets, distanceHexes, gridRenderer);
    }
  }

  // Целевые ячейки для продвижения: чужие кварталы (когда армия готова к агрессии)
  // + неконтролируемые ключевые точки (в пределах радиуса экспансии архетипа)
  const targetHexKeys = [];
  if (isAggressionReady) {
    enemyQuarters.forEach((cityInfo, quarterHexKey) => {
      // Дальний нейтральный город во второй половине — не цель для агрессоров
      if (ownUnitDistancesForTargets
        && cityInfo.color === NEUTRAL_PLAYER_COLOR
        && (ownUnitDistancesForTargets.get(quarterHexKey) ?? Infinity) > 3
      ) {
        return;
      }
      if (homeDistances) {
        const homeDistance = homeDistances.get(quarterHexKey);
        if (homeDistance === undefined || homeDistance > profile.expansionRadius) {
          return;
        }
      }
      // Инсектоиды/крысолюды: только кварталы города-фокуса (рой не расползается);
      // без фокуса (вокруг лишь агрессоры) города не цель — центр и оборона
      if ((isInsectoidsPlayer || isRatmenPlayer)
        && (!cityFocusQuarters || !cityFocusQuarters.has(quarterHexKey))
      ) {
        return;
      }
      // Демоны в ранней игре: города агрессивных фракций — не цель
      if (demonsExcludedQuarters && demonsExcludedQuarters.has(quarterHexKey)) {
        return;
      }
      // Волна 22 (21.16): нейтральные города не тянут отряды ОБЩИМ градиентом —
      // к ним ведут только настоящие CAPTURE-кампании и фокус роя (иначе отряды
      // вечно стоят на пустых ячейках у нейтрального города, который никто не
      // собирается брать)
      if (cityInfo.color === NEUTRAL_PLAYER_COLOR
        && (!cityFocusQuarters || !cityFocusQuarters.has(quarterHexKey))
      ) {
        return;
      }
      // Волна 22 (21.9b): нейтральный город с путем влияния — не цель тяготения
      if (influenceConnectedNeutralQuarters && influenceConnectedNeutralQuarters.has(quarterHexKey)) {
        return;
      }
      // Волна 25 (23.8): пустой город с уже стоящим на квартале своим отрядом
      // не тянет дополнительные отряды общим градиентом
      if (oversubscribedCaptureQuarters && oversubscribedCaptureQuarters.has(quarterHexKey)) {
        return;
      }
      targetHexKeys.push(quarterHexKey);
    });
  }
  // Волна 35 (K): ближайший агрессивный сосед — соперник с наибольшей силой у своих
  // городов. Его города — первые среди захватных целей (updateCampaigns), его
  // отряды у своих городов — цели общего градиента при готовности к агрессии
  // (случай Вергилия, 22.10 R7: армия Катона и его пустой город -8,4 в 2–4
  // клетках, а три d6 маршировали на 14–16 клеток к другому сопернику)
  const w35AggressiveNeighbor = w35CampaignRangeActive && ownQuarters.size > 0
    ? findNearestAggressiveNeighbor({
      ownQuarterKeys: [...ownQuarters.keys()],
      units,
      players,
      currentPlayerId: currentPlayer.id,
      maxDistance: W35_AGGRESSIVE_NEIGHBOR_DISTANCE,
    })
    : null;
  if (isAggressionReady && w35AggressiveNeighbor) {
    w35AggressiveNeighbor.unitHexKeys.forEach((hexKey) => targetHexKeys.push(hexKey));
  }
  Object.entries(hexes).forEach(([hexKey, token]) => {
    if (checkIfKeyPointToken(token) && !controlledKeyPoints.has(hexKey)) {
      if (homeDistances) {
        const homeDistance = homeDistances.get(hexKey);
        if (homeDistance === undefined || homeDistance > profile.expansionRadius) {
          return;
        }
      }
      targetHexKeys.push(hexKey);
    }
  });

  // Направление армии = направление строительства: ячейки последних построек
  // (фронт экспансии — центральные кварталы, дороги к руинам, рудники) тянут
  // отряды агрессивных профилей на прикрытие захваченного и дальнейший пуш.
  // Волна 33 (M5, только strongCities): у бонусного бота связь обратная —
  // стройка идет к сформированным отрядам (distanceFactor в decideConstructionStep),
  // а армию к разбросанным постройкам не тянем: именно эта тяга разводила
  // ее по карте вслед за кварталами у дальних ключевых точек
  if ((profile.military > 1 || profile.captureEagerness >= 1) && !(strongCities && bonusAxis)) {
    (strategy.recentBuildCells || []).forEach((cellKey) => {
      if (gridRenderer.hexes[cellKey]) {
        targetHexKeys.push(cellKey);
      }
    });
  }

  // Волна 19 (18.9): без угрозы городу квартал не должен быть приоритетнее
  // соседних ячеек — целью тяготения добавляем и свободных соседей квартала,
  // выравнивая градиент (BFS даст 0 и кварталу, и соседям)
  const pushOwnQuarterTarget = (quarterHexKey) => {
    targetHexKeys.push(quarterHexKey);
    if (ownQuartersUnderAttack.size > 0) {
      return;
    }
    getNeighborHexKeys(quarterHexKey, gridRenderer).forEach((adjKey) => {
      if (gridRenderer.hexes[adjKey] && !hexes[adjKey]) {
        targetHexKeys.push(adjKey);
      }
    });
  };

  // Корреляция армии с направлением развития: свои ПЕРЕДОВЫЕ кварталы (ближайшие
  // к противнику) — цели тяготения для отрядов без лучших задач, чтобы армия
  // прикрывала фронт, куда бот строится, а не дрейфовала в тыл
  // Волна 20 (19.1/19.2): дистанции до прифронтовых (ближайших к ЖИВОМУ,
  // не нейтральному, противнику) своих городов — для сильного тяготения
  // крупных простаивающих отрядов (см. idleArmyFrontierDistances ниже)
  let idleArmyFrontierDistances = null;
  if (enemyQuarters.size > 0 && ownQuarters.size > 0) {
    const enemyDistancesForForward = calcBfsDistances([...enemyQuarters.keys()], distanceHexes, gridRenderer);
    let minOwnEnemyDistance = Infinity;
    ownQuarters.forEach((cityInfo, quarterHexKey) => {
      const distance = enemyDistancesForForward.get(quarterHexKey);
      if (distance !== undefined && distance < minOwnEnemyDistance) {
        minOwnEnemyDistance = distance;
      }
    });
    if (minOwnEnemyDistance < Infinity) {
      ownQuarters.forEach((cityInfo, quarterHexKey) => {
        const distance = enemyDistancesForForward.get(quarterHexKey);
        if (distance !== undefined && distance <= minOwnEnemyDistance + 2) {
          pushOwnQuarterTarget(quarterHexKey);
        }
      });
    }

    const nonNeutralOwnEnemyDistances = calcBfsDistances(
      [...enemyQuarters.entries()]
        .filter(([, cityInfo]) => cityInfo.color !== NEUTRAL_PLAYER_COLOR)
        .map(([quarterHexKey]) => quarterHexKey),
      distanceHexes, gridRenderer
    );
    let bestFrontierQuarter = null;
    let bestFrontierDistance = Infinity;
    ownQuarters.forEach((cityInfo, quarterHexKey) => {
      const distance = nonNeutralOwnEnemyDistances.get(quarterHexKey);
      if (distance !== undefined && distance < bestFrontierDistance) {
        bestFrontierDistance = distance;
        bestFrontierQuarter = quarterHexKey;
      }
    });
    // Волна 33 (M3, только strongCities): источники тяги — кварталы и казармы
    // кузницы оси (город, где строится кластер казарм/лагерь/арсенал), а не один
    // ближайший к врагу квартал: отряды кластеризуются у кузницы, а не толпятся
    // вокруг одного гекса. Кузница потеряна посреди раунда — прежний расчет
    const bonusStagingPullSources = strongCities && bonusAxis
      ? collectStagingPullSources((citiesCache?.[currentPlayer.color] || [])
        .find((city) => city.centralQuarter === bonusAxis.stagingCityKey))
      : [];
    if (bonusStagingPullSources.length > 0) {
      idleArmyFrontierDistances = calcBfsDistances(bonusStagingPullSources, distanceHexes, gridRenderer);
    } else if (bestFrontierQuarter) {
      idleArmyFrontierDistances = calcBfsDistances([bestFrontierQuarter], distanceHexes, gridRenderer);
    }
  }

  // Захваченный центр без гарнизона тянет армию (нюанс живого теста 11):
  // свои кварталы центральной зоны, где нет своего отряда на месте или смежно,
  // становятся целями продвижения — оборона и развитие успеха в зоне конкуренции
  ownQuarters.forEach((cityInfo, quarterHexKey) => {
    if (!isHegemonismBonusHexKey(quarterHexKey)) {
      return;
    }
    const hasGarrison = units.some((unit) =>
      unit.ownerId === currentPlayer.id
      && (unit.hexKey === quarterHexKey
        || getNeighborHexKeys(quarterHexKey, gridRenderer).includes(unit.hexKey))
    );
    if (!hasGarrison) {
      pushOwnQuarterTarget(quarterHexKey);
    }
  });

  if (cityThreatByQuarter) {
    // Волна 32 (оборона): общая тяга армии — только к городам, которые иначе
    // захватят в ЭТОМ раунде и где нет держателя. Риск следующего раунда закрывает
    // одна оборонная кампания ближайшего отряда (updateCampaigns): лишние свои
    // цели гасят общий градиент к чужим городам
    w32CityThreats.forEach((threat) => {
      if (threat.level === CITY_THREAT_LEVELS.THIS_ROUND && threat.holders.length === 0) {
        threat.quarters.forEach((quarterHexKey) => targetHexKeys.push(quarterHexKey));
      }
    });
  } else if (imminentThreatQuarters) {
    // Волна 25 (23.1): кварталы под захватом следующим ходом — цели тяготения
    // для всей армии (градиент тянет и дальние отряды, а не только соседние)
    imminentThreatQuarters.forEach((quarterHexKey) => {
      targetHexKeys.push(quarterHexKey);
    });
  }

  // Волна 23 (22.2): HEGEMONISM — вся армия стягивается в бонусную зону
  // центра (радиус 2): оборона центральной зоны, где политика дает очки
  const hasHegemonismForMovement = isPlayerPolicy(currentPlayer, POLICIES.HEGEMONISM);
  if (hasHegemonismForMovement) {
    POLICY_HEGEMONISM_BONUS_HEX_KEYS.forEach((bonusHexKey) => {
      if (gridRenderer.hexes[bonusHexKey]) {
        targetHexKeys.push(bonusHexKey);
      }
    });
  }

  const targetDistances = calcBfsDistances(targetHexKeys, distanceHexes, gridRenderer);

  // Отдельный, более слабый градиент к кварталам игрока под давлением ботов:
  // без него множители выше работали бы только у тех отрядов, что уже рядом
  const pressureQuarterHexKeys = pressuredEnemyColors.size > 0
    ? [...enemyQuarters.entries()]
      .filter(([, cityInfo]) => pressuredEnemyColors.has(cityInfo.color))
      .map(([quarterHexKey]) => quarterHexKey)
    : [];
  const pressureTargetDistances = pressureQuarterHexKeys.length > 0
    ? calcBfsDistances(pressureQuarterHexKeys, distanceHexes, gridRenderer)
    : null;
  // Волна 33 (M2, только strongCities): такой же слабый градиент к кварталам
  // ближайшего противника оси — BFS уже посчитана осью на раунд
  const bonusTargetDistances = strongCities && bonusAxis ? bonusAxis.enemyColorQuarterDistances : null;

  // Радиус снабжения своих ферм: уходить за него — голодать.
  // При ЭКОАНАРХИЗМЕ снабжение достает отряды где угодно — штраф не нужен.
  const playerFarms = getPlayerFarms(state);
  const hasEcoanarchismForSupply = isPlayerPolicy(currentPlayer, POLICIES.ECOANARCHISM);
  const supplyReachCells = playerFarms.length > 0 && !hasEcoanarchismForSupply
    ? calcSupplyReachCells(playerFarms, hexes, gridRenderer)
    : null;

  // Волна 28 (25.3): досягаемость supplyReachCells чисто геометрическая — не
  // учитывает, что 1 ферма кормит только 1 отряд за раунд (2 со складом).
  // common_problems_25: второй д10 эльфов вставал в клетку в радиусе уже
  // занятой ЕДИНСТВЕННОЙ фермы и голодал каждую фазу снабжения, хотя формально
  // был "в снабжении". farmBudgetSlots считает вместимость (реюзается BFS на
  // ферму, дорогая часть); calcBudgetAwareSupplyReachCells — жадное распределение
  // слотов между СВОИМИ отрядами (кроме excludedUnitId — это тот, для которого
  // сейчас выбирается позиция) по числу досягаемых ферм (меньше — выше приоритет,
  // как в decideConstructionStep), возвращает ячейки с реально свободным слотом.
  // Гейт вынесен ДО вычислений (а не только на использование в scoreUnitPosition) —
  // лишний BFS на ферму не должен тратиться на partie legacy-группы, которая
  // результат все равно не использует
  const wave28FarmSupplyBudgetActive = !isWave27BehaviorPlayer(currentPlayer.id)
    && !WAVE28_FARM_SUPPLY_BUDGET_EXCLUDED_FACTIONS.has(currentPlayer.factionId);
  const specialBuildingsForSupplyBudget = wave28FarmSupplyBudgetActive ? selectSpecialBuildings(state) : null;
  // Волна 33 (M9, только strongCities): досягаемость фермы — по relay-модели
  // (calcSupplyReachCellsWithRelay: глубина перезапускается на своей ферме, как в
  // calculateAvailableSupplyCells): емкость тыловой фермы доходит до отрядов у
  // кузницы по цепочке ферм-ретрансляторов, и бюджет не штрафует их зря
  const ownFarmKeysForRelay = strongCities && bonusAxis ? new Set(playerFarms) : null;
  const farmBudgetSlots = wave28FarmSupplyBudgetActive && playerFarms.length > 0 && !hasEcoanarchismForSupply
    ? playerFarms.map((farmHexKey) => ({
      capacity: getNeighborHexKeys(farmHexKey, gridRenderer).some(
        (adjKey) => specialBuildingsForSupplyBudget[adjKey] === SPECIAL_BUILDING_TOKENS.WAREHOUSE
      ) ? 2 : 1,
      reach: ownFarmKeysForRelay
        ? calcSupplyReachCellsWithRelay(farmHexKey, ownFarmKeysForRelay, hexes, gridRenderer)
        : calcSupplyReachCells([farmHexKey], hexes, gridRenderer),
    }))
    : null;
  const calcBudgetAwareSupplyReachCells = (excludedUnitId) => {
    if (!farmBudgetSlots) {
      return null;
    }
    const slots = farmBudgetSlots.map((slot) => ({...slot}));
    const claimants = getPlayerUnits(currentPlayer, units)
      .filter((otherUnit) => otherUnit.id !== excludedUnitId && !isUnitInsectoid(otherUnit))
      .map((otherUnit) => ({
        reachableSlots: slots.filter((slot) => slot.reach.has(otherUnit.hexKey)),
      }))
      .sort((a, b) => a.reachableSlots.length - b.reachableSlots.length);
    claimants.forEach(({reachableSlots}) => {
      const slotWithBudget = reachableSlots.find((slot) => slot.capacity > 0);
      if (slotWithBudget) {
        slotWithBudget.capacity -= 1;
      }
    });
    const reachCells = new Set();
    slots.forEach((slot) => {
      if (slot.capacity > 0) {
        slot.reach.forEach((cellKey) => reachCells.add(cellKey));
      }
    });
    return reachCells;
  };

  // Фокус захватных кампаний: город-фокус роя/волны, либо (демоны в ранней
  // игре) все города, кроме принадлежащих агрессивным фракциям
  let focusCaptureTargets = cityFocusQuarters;
  if (demonsExcludedQuarters && demonsExcludedQuarters.size > 0) {
    focusCaptureTargets = new Set(
      [...enemyQuarters.keys()].filter((quarterHexKey) => !demonsExcludedQuarters.has(quarterHexKey))
    );
  }

  // Волна 19 (18.11): d20 размера 14+ — таран: агрессивно идет на захват
  // ближайшего города ЖИВОГО противника (не нейтрального); дороги ему
  // обеспечивает фаза строительства через градиент кампании
  const d20PushTargets = new Map();
  if (enemyQuarters.size > 0) {
    getPlayerUnits(currentPlayer, units).forEach((unit) => {
      // Волна 35 (G): гарнизон в форсированных маршах не участвует
      if (unit.type !== UNIT_TYPES.D20.id || unit.size < 14 || isW35Garrison(unit.id)) {
        return;
      }
      const unitDistances = calcBfsDistances([unit.hexKey], distanceHexes, gridRenderer);
      let bestQuarter = null;
      let bestDistance = Infinity;
      enemyQuarters.forEach((cityInfo, quarterHexKey) => {
        if (cityInfo.color === NEUTRAL_PLAYER_COLOR) {
          return;
        }
        // Волна 25 (23.8): город, чей захват уже обеспечен своим отрядом
        if (oversubscribedCampaignQuarters && oversubscribedCampaignQuarters.has(quarterHexKey)) {
          return;
        }
        const distance = unitDistances.get(quarterHexKey);
        if (distance !== undefined && distance < bestDistance) {
          bestDistance = distance;
          bestQuarter = quarterHexKey;
        }
      });
      if (bestQuarter) {
        d20PushTargets.set(unit.id, bestQuarter);
      }
    });
  }

  // Волна 20 (19.5): зверолюды размера 80%+ максимума обязаны кросс-раундово
  // маршировать к ближайшему ЖИВОМУ противнику (в приоритете не агрессивная
  // фракция) — инициировать ближний бой, а не ждать у себя дома
  if (isPlayerFaction(currentPlayer, FACTIONS.BEASTMEN) && enemyQuarters.size > 0) {
    getPlayerUnits(currentPlayer, units).forEach((unit) => {
      if (d20PushTargets.has(unit.id) || isW35Garrison(unit.id)) {
        return;
      }
      const beastmenMaxSize = getUnitTypeConfig(unit.type)?.maxSize || 0;
      if (beastmenMaxSize === 0 || unit.size < beastmenMaxSize * 0.8) {
        return;
      }
      const unitDistances = calcBfsDistances([unit.hexKey], distanceHexes, gridRenderer);
      const findNearestQuarter = (predicate) => {
        let best = null;
        let bestDistance = Infinity;
        enemyQuarters.forEach((cityInfo, quarterHexKey) => {
          if (cityInfo.color === NEUTRAL_PLAYER_COLOR || !predicate(cityInfo)) {
            return;
          }
          // Волна 25 (23.8): город, чей захват уже обеспечен своим отрядом
          if (oversubscribedCampaignQuarters && oversubscribedCampaignQuarters.has(quarterHexKey)) {
            return;
          }
          const distance = unitDistances.get(quarterHexKey);
          if (distance !== undefined && distance < bestDistance) {
            bestDistance = distance;
            best = quarterHexKey;
          }
        });
        return best;
      };
      const isNonAggressiveOwner = (cityInfo) => {
        const owner = players.find((otherPlayer) => otherPlayer.color === cityInfo.color);
        const primaryArchetype = owner ? (getFactionPreferences(owner.factionId).archetypes[0] || '') : '';
        return !primaryArchetype.startsWith('AGGRESSIVE');
      };
      // Волна 35 (K): неагрессивный владелец — только если не намного дальше ближайшего
      const bestQuarter = w35CampaignRangeActive
        ? pickW35ForcedMarchQuarter(
          findNearestQuarter(isNonAggressiveOwner), findNearestQuarter(() => true), unitDistances
        )
        : findNearestQuarter(isNonAggressiveOwner) || findNearestQuarter(() => true);
      if (bestQuarter) {
        d20PushTargets.set(unit.id, bestQuarter);
      }
    });
  }

  // Волна 21 (20.1, запечено): крысолюды размера 3+ обязаны кросс-раундово маршировать
  // к ближайшему ЖИВОМУ противнику (приоритет — не орки/крысолюды/инсектоиды,
  // см. RATMEN_RUSH_LOW_PRIORITY_OWNERS) — живой тест 20: часто сидели в
  // глухой обороне. Цель регистрируется как настоящая CAPTURE-кампания
  // (forcedCaptureByUnit ниже) — это же автоматически тянет строительство
  // дорог к ней (П11, campaignDestinationDistances), отдельного кода для
  // "пути к городу противника" не требуется
  if (isPlayerFaction(currentPlayer, FACTIONS.RATMEN) && enemyQuarters.size > 0) {
    getPlayerUnits(currentPlayer, units).forEach((unit) => {
      if (d20PushTargets.has(unit.id) || unit.size < 3 || isW35Garrison(unit.id)) {
        return;
      }
      const unitDistances = calcBfsDistances([unit.hexKey], distanceHexes, gridRenderer);
      const findNearestQuarter = (predicate) => {
        let best = null;
        let bestDistance = Infinity;
        enemyQuarters.forEach((cityInfo, quarterHexKey) => {
          if (cityInfo.color === NEUTRAL_PLAYER_COLOR || !predicate(cityInfo)) {
            return;
          }
          // Волна 25 (23.8): город, чей захват уже обеспечен своим отрядом
          if (oversubscribedCampaignQuarters && oversubscribedCampaignQuarters.has(quarterHexKey)) {
            return;
          }
          const distance = unitDistances.get(quarterHexKey);
          if (distance !== undefined && distance < bestDistance) {
            bestDistance = distance;
            best = quarterHexKey;
          }
        });
        return best;
      };
      const isPriorityOwner = (cityInfo) => {
        const owner = players.find((otherPlayer) => otherPlayer.color === cityInfo.color);
        return !owner || !RATMEN_RUSH_LOW_PRIORITY_OWNERS.has(owner.factionId);
      };
      // Волна 35 (K): приоритетный владелец — только если не намного дальше ближайшего
      const bestQuarter = w35CampaignRangeActive
        ? pickW35ForcedMarchQuarter(findNearestQuarter(isPriorityOwner), findNearestQuarter(() => true), unitDistances)
        : findNearestQuarter(isPriorityOwner) || findNearestQuarter(() => true);
      if (bestQuarter) {
        d20PushTargets.set(unit.id, bestQuarter);
      }
    });
  }

  // Волна 23 (22.5): инсектоидам нейтральные городские кварталы исключаются
  // из захватных кампаний ПОЛНОСТЬЮ (не только города с путями влияния)
  let campaignExcludedQuarters = influenceConnectedNeutralQuarters;
  if (isInsectoidsPlayer) {
    campaignExcludedQuarters = new Set(influenceConnectedNeutralQuarters || []);
    enemyQuarters.forEach((cityInfo, quarterHexKey) => {
      if (cityInfo.color === NEUTRAL_PLAYER_COLOR) {
        campaignExcludedQuarters.add(quarterHexKey);
      }
    });
  }
  // Волна 25 (23.8): кварталы «перезаписанных» захватов — вне кампаний: чужие
  // кампании на них отменятся валидацией, отряды освободятся под другие цели
  if (oversubscribedCampaignQuarters) {
    campaignExcludedQuarters = new Set(campaignExcludedQuarters || []);
    oversubscribedCampaignQuarters.forEach(
      (quarterHexKey) => campaignExcludedQuarters.add(quarterHexKey)
    );
  }

  // Волна 27 (24.4, запечена): крысолюды во второй половине агрессивно забирают центр,
  // когда там некому составить им конкуренцию — неконтролируемые ключевые
  // точки центра (радиус 3) становятся целями захватных кампаний, если сила
  // живых противников в центре (радиус 4) не превышает армию роя
  let ratmenCenterTargets = null;
  if (isRatmenPlayer && selectRound(state) > FINAL_ROUND / 2) {
    const enemyStrengthNearCenter = units.reduce((sum, unit) => {
      if (unit.ownerId === currentPlayer.id || unit.ownerId === NEUTRAL_PLAYER_ID) {
        return sum;
      }
      return calcHexDistance(unit.hexKey, FIELD_CENTRAL_HEX_KEY) <= 4 ? sum + unit.size : sum;
    }, 0);
    if (enemyStrengthNearCenter <= ownArmyStrength) {
      const controlledKeyPointsForCenter = getControlledKeyPointHexKeys(citiesCache);
      ratmenCenterTargets = new Set();
      Object.entries(hexes).forEach(([hexKey, token]) => {
        if (checkIfKeyPointToken(token)
          && !controlledKeyPointsForCenter.has(hexKey)
          && calcHexDistance(hexKey, FIELD_CENTRAL_HEX_KEY) <= 3
        ) {
          ratmenCenterTargets.add(hexKey);
        }
      });
      if (ratmenCenterTargets.size === 0) {
        ratmenCenterTargets = null;
      }
    }
  }

  // Волна 23 (22.2): HEGEMONISM при значительной доминации сил — часть армии
  // идет на захват ближайших к центру городов живых противников
  let hegemonismCaptureTargets = null;
  if (hasHegemonismForMovement
    && ownArmyStrength >= calcStrongestOpponentArmy(currentPlayer.id, players, units) * 1.5
  ) {
    hegemonismCaptureTargets = new Set();
    enemyQuarters.forEach((cityInfo, quarterHexKey) => {
      if (cityInfo.color !== NEUTRAL_PLAYER_COLOR
        && calcHexDistance(quarterHexKey, FIELD_CENTRAL_HEX_KEY) <= 4
      ) {
        hegemonismCaptureTargets.add(quarterHexKey);
      }
    });
  }

  const extraCaptureTargetsForCampaigns = ratmenCenterTargets || hegemonismCaptureTargets
    ? new Set([...(hegemonismCaptureTargets || []), ...(ratmenCenterTargets || [])])
    : null;

  // Новая архитектура (планировщик раунда, A/B): скоординированные группы
  // атаки — форс-цели группы перекрывают одиночные цели d20/крысолюдов.
  // С включенными роллаутами (__plannerRollouts) план выбирается прогоном
  // кандидатов в песочнице; внутри самой песочницы план читается из кэша
  // (кандидат) или строится дефолтно — вложенных роллаутов нет.
  let forcedTargetsForCampaigns = d20PushTargets;
  if (getPlannerMode(state, currentPlayer.id).enabled) {
    const plannerDeps = {
      currentPlayer,
      units,
      players,
      citiesCache,
      hexes,
      gridRenderer,
      round: selectRound(state),
      state,
    };
    const roundPlanForMovement = ensureRoundPlan(plannerDeps, Boolean(store.__isBotSandbox));
    if (roundPlanForMovement.assaultTargets.size > 0) {
      forcedTargetsForCampaigns = new Map([...d20PushTargets, ...roundPlanForMovement.assaultTargets]);
      // Волна 35 (G): штурм группы планировщика гарнизон тоже не уводит
      if (w35GarrisonByUnitId) {
        w35GarrisonByUnitId.forEach((garrison, unitId) => forcedTargetsForCampaigns.delete(unitId));
      }
    }
  }

  // Казарменные выдержки считаются до кампаний (волна 32, логистика: удерживаемые
  // отряды не берут захват и осаду); от кампаний они не зависят
  const ownBarracksCells = new Set((citiesCache?.[currentPlayer.color] || []).flatMap(
    (city) => city[COMMON_BUILDING_TOKENS.BARRACKS] || []
  ));

  // Волна 37 (B1, B2): свои казармы (рабочая ли, рабочий ли лагерь рядом) и потеря
  // роста отряда за уход с казармы — по правилам снабжения: с бюджетом ферм волны
  // 28 (отряд сам себе слот не занимает), при ЭКОАНАРХИЗМЕ — снабжение везде, в
  // последнем раунде — роста нет
  const w37BarracksInfo = isW37BarracksStayActive || isW37CampHoldActive
    ? buildW37BarracksInfo({
      barracksHexKeys: [...ownBarracksCells],
      specialBuildings: selectSpecialBuildings(state),
      getNeighbors: (hexKey) => getNeighborHexKeys(hexKey, gridRenderer),
      isWorkingBarracks: (hexKey) => checkIfOnWorkingBarracks(
        hexKey, hexes, selectSpecialBuildings(state), currentPlayer, citiesCache, units, selectBuildingsDamage(state)
      ),
      isWorkingCampNear: (hexKey) => checkIfAdjacentToWorkingMilitaryCamp(
        hexKey, currentPlayer, hexes, selectSpecialBuildings(state), citiesCache, units, selectBuildingsDamage(state)
      ),
    })
    : null;
  const isW37FinalRound = w37BarracksInfo ? checkIfFinalRound(state) : false;
  const isW37SupplyEverywhere = playerFarms.length > 0 && hasEcoanarchismForSupply;
  const w37GrowthDeltaByUnitId = new Map();
  const calcW37UnitGrowthDelta = (unit, barracksHexKey) => {
    const memoKey = `${unit.id}@${barracksHexKey}`;
    if (!w37GrowthDeltaByUnitId.has(memoKey)) {
      w37GrowthDeltaByUnitId.set(memoKey, calcW37GrowthDeltaAt({
        unit,
        owner: currentPlayer,
        cellKey: barracksHexKey,
        barracksInfo: w37BarracksInfo,
        supplyCells: calcBudgetAwareSupplyReachCells(unit.id) || supplyReachCells,
        isSupplyEverywhere: isW37SupplyEverywhere,
        isFinalRound: isW37FinalRound,
      }));
    }
    return w37GrowthDeltaByUnitId.get(memoKey);
  };
  // B1: резервные держатели и запрет кварталов (collectW37BarracksStay); города
  // резерва — вне целей обороны NEXT_ROUND (updateCampaigns), M4 — только при
  // угрозе этого раунда
  const w37BarracksStayByHexKey = isW37BarracksStayActive
    ? collectW37BarracksStay({
      ownCities: citiesCache?.[currentPlayer.color] || [],
      cityThreatByCityKey: new Map(w37ThisRound.cityThreats.map((threat) => [threat.city.centralQuarter, threat])),
      units,
      currentPlayerId: currentPlayer.id,
      barracksInfo: w37BarracksInfo,
      getNeighbors: (hexKey) => getNeighborHexKeys(hexKey, gridRenderer),
      minFreeQuarters: w37Knobs.reserveMinFreeQuarters,
      getGrowthDelta: calcW37UnitGrowthDelta,
    })
    : null;
  let w37ReserveCityKeys = null;
  let w37ThisRoundCityKeys = null;
  if (w37BarracksStayByHexKey) {
    w37ReserveCityKeys = new Set();
    w37BarracksStayByHexKey.forEach((entry) => entry.reserveCities.forEach(
      (reserveCity) => w37ReserveCityKeys.add(reserveCity.cityKey)
    ));
    w37ThisRoundCityKeys = new Set(w37ThisRound.cityThreats
      .filter((threat) => checkIfW37ThisRoundThreat(threat))
      .map((threat) => threat.city.centralQuarter));
  }
  // B2: свои отряды на своей казарме у лагеря, которые на ней растут быстрее, —
  // удержание роста (тяга к фронту их не уводит, контроль прогресса L4 их ждет)
  let w37CampHoldUnitIds = null;
  if (isW37CampHoldActive) {
    w37CampHoldUnitIds = new Set();
    getPlayerUnits(currentPlayer, units).forEach((unit) => {
      const barracksInfo = w37BarracksInfo.get(unit.hexKey);
      if (barracksInfo && barracksInfo.isCampAdjacent
        && hexes[unit.hexKey] === COMMON_BUILDING_TOKENS.BARRACKS
        && calcW37UnitGrowthDelta(unit, unit.hexKey) > 0
      ) {
        w37CampHoldUnitIds.add(unit.id);
      }
    });
  }

  // Лепреконы: d10/d20 с размером < 40% максимума не покидают свою казарму —
  // иначе не успеют накопить максимальную численность (живой тест 14; порог
  // калиброван 50% -> 40% по отчету №9 — выдержка передерживала армию).
  // Исключения: готовность к агрессии (армия обязана маршировать), угроза городу
  // казармы, достижимый незащищенный чужой квартал (фильтр ячеек в цикле).
  // Волна 33 (M1): готовность по штатным правилам, без армейской фазы бонусного
  // бота — у обычных ботов она равна isAggressionReady
  const leprechaunHoldThreshold = 0.4;
  const leprechaunHoldUnits = new Set();
  if (isLeprechaunsPlayer && !isAggressionReadyByRules) {
    const threatenedBarracksCells = new Set();
    (citiesCache?.[currentPlayer.color] || []).forEach((city) => {
      const cityBarracks = city[COMMON_BUILDING_TOKENS.BARRACKS] || [];
      if (cityBarracks.length === 0) {
        return;
      }
      const cityQuarters = getAllQuartersOfCity(city);
      const isCityThreatened = units.some((otherUnit) =>
        otherUnit.ownerId !== currentPlayer.id
        && otherUnit.ownerId !== NEUTRAL_PLAYER_ID
        && cityQuarters.some((quarterHexKey) => calcHexDistance(otherUnit.hexKey, quarterHexKey) <= 3)
      );
      if (isCityThreatened) {
        cityBarracks.forEach((cellKey) => threatenedBarracksCells.add(cellKey));
      }
    });
    playerUnits.forEach((unit) => {
      if (unit.type !== UNIT_TYPES.D10.id && unit.type !== UNIT_TYPES.D20.id) {
        return;
      }
      if (!ownBarracksCells.has(unit.hexKey) || threatenedBarracksCells.has(unit.hexKey)) {
        return;
      }
      if (unit.size < (getUnitTypeConfig(unit.type)?.maxSize || 0) * leprechaunHoldThreshold) {
        leprechaunHoldUnits.add(unit.id);
      }
    });
  }

  // Волна 23 (22.6): эльфы с PATRIOTISM держат отряды на своей казарме до 60%
  // максимального размера — скидка политики на найм размера доводит их до
  // боеспособной численности прежде, чем отряд уходит в поле
  const elfPatriotismHoldUnits = new Set();
  if (isPlayerFaction(currentPlayer, FACTIONS.ELVES)
    && isPlayerPolicy(currentPlayer, POLICIES.PATRIOTISM)
  ) {
    playerUnits.forEach((unit) => {
      if (!ownBarracksCells.has(unit.hexKey)) {
        return;
      }
      // Казарма города под прямой угрозой — отряд свободен оборонять
      const isBarracksThreatened = units.some((otherUnit) =>
        otherUnit.ownerId !== currentPlayer.id
        && otherUnit.ownerId !== NEUTRAL_PLAYER_ID
        && calcHexDistance(otherUnit.hexKey, unit.hexKey) <= 3);
      if (isBarracksThreatened) {
        return;
      }
      if (unit.size < (getUnitTypeConfig(unit.type)?.maxSize || 0) * 0.6) {
        elfPatriotismHoldUnits.add(unit.id);
      }
    });
  }

  // Волна 19 (18.12): свеженанятый d12 (размер < 6) не уходит с казармы в
  // опасные места — размер 1-5 сгорает в первом же контакте. Исключения:
  // гарантированный захват незащищенного чужого квартала и оборона своего города
  const d12HoldUnits = new Set();
  playerUnits.forEach((unit) => {
    if (unit.type !== UNIT_TYPES.D12.id || unit.size >= 6) {
      return;
    }
    if (!ownBarracksCells.has(unit.hexKey)) {
      return;
    }
    d12HoldUnits.add(unit.id);
  });

  // Волна 32: отряды на жестком казарменном удержании — одно множество на тик для
  // контроля прогресса L4 (вне захвата и осады) и обороны (оборона только своего города)
  const w32HeldUnitIds = w32StallActive || w32CityThreats !== null
    ? new Set([...leprechaunHoldUnits, ...elfPatriotismHoldUnits, ...d12HoldUnits])
    : null;
  // Волна 32 (логистика): свои отряды вне конверта снабжения (L3: их захватная кампания
  // переживает потерю готовности к агрессии) и мягкое удержание роста на казарме
  // (L4: не застревание кампании) — условие повторяет бонус STAY_ON_BARRACKS_TO_GROW
  // в scoreUnitPosition (context.round в перемещении не задан, позднего снижения
  // порога там нет)
  let w32OutOfSupplyUnitIds = null;
  let w32GrowthHoldUnitIds = null;
  if (w32MarchActive || w32StallActive) {
    const allPlayerUnits = getPlayerUnits(currentPlayer, units);
    if (w32MarchActive && supplyReachCells) {
      w32OutOfSupplyUnitIds = new Set(allPlayerUnits
        .filter((unit) => !isUnitInsectoid(unit) && !supplyReachCells.has(unit.hexKey))
        .map((unit) => unit.id));
    }
    if (w32StallActive && ownQuarters.size > 0 && ownBarracksCells.size > 0) {
      w32GrowthHoldUnitIds = new Set();
      const specialBuildingsForGrowthHold = selectSpecialBuildings(state) || {};
      allPlayerUnits.forEach((unit) => {
        if (!ownBarracksCells.has(unit.hexKey)
          || hexes[unit.hexKey] !== COMMON_BUILDING_TOKENS.BARRACKS
          || unit.factionId === FACTIONS.ELVES
          || unit.factionId === FACTIONS.RATMEN
        ) {
          return;
        }
        const isCampAdjacent = getNeighborHexKeys(unit.hexKey, gridRenderer).some(
          (adjKey) => specialBuildingsForGrowthHold[adjKey] === SPECIAL_BUILDING_TOKENS.MILITARY_CAMP
        );
        // Волна 37 (B2): у лагеря удержание — по разнице роста, как бонус в оценке
        if (w37CampHoldUnitIds && isCampAdjacent) {
          if (w37CampHoldUnitIds.has(unit.id)) {
            w32GrowthHoldUnitIds.add(unit.id);
          }
          return;
        }
        const maxSize = getUnitTypeConfig(unit.type)?.maxSize || 0;
        if (unit.size < Math.ceil(maxSize * (isCampAdjacent ? 0.8 : 0.6))) {
          w32GrowthHoldUnitIds.add(unit.id);
        }
      });
    }
  }

  // Волна 33 (M4, только strongCities): фронтирный город без гарнизона, к
  // кварталам которого подошел живой противник (радиус garrisonThreatRadius), —
  // свой отряд на казарме этого города снимается с удержания роста (контроль
  // прогресса L4, тяга к кузнице M3) и с бонуса роста на казарме в оценке ячейки
  // (bonusThreatReleasedUnitIds в контексте): гарнизонное удержание квартала
  // тогда выигрывает у казармы, а без угрозы отряд дорастает. У legacy набора
  // роста нет — исключать не из чего, а набор угрозы не считается вовсе
  let bonusThreatReleasedUnitIds = null;
  if (strongCities && bonusAxis && bonusGarrisonByQuarter.size > 0) {
    bonusThreatReleasedUnitIds = findThreatenedGarrisonBarracksUnitIds({
      garrisonByQuarter: bonusGarrisonByQuarter,
      units,
      currentPlayerId: currentPlayer.id,
      threatRadius: bonusProfile.garrisonThreatRadius,
      // Волна 37 (B1): рост снимается только при угрозе этого раунда; у legacy — радиус
      isCityThreatened: w37ThisRoundCityKeys ? (cityKey) => w37ThisRoundCityKeys.has(cityKey) : null,
    });
    if (w32GrowthHoldUnitIds) {
      bonusThreatReleasedUnitIds.forEach((unitId) => w32GrowthHoldUnitIds.delete(unitId));
    }
    if (w37CampHoldUnitIds) {
      bonusThreatReleasedUnitIds.forEach((unitId) => w37CampHoldUnitIds.delete(unitId));
    }
  }

  // Кампании: многораундовые цели отрядов (захват/осада/оборона)
  const {campaignByUnit, campaignDistances} = updateCampaigns({
    currentPlayer,
    strategy,
    units,
    citiesCache,
    players,
    hexes,
    gridRenderer,
    isAggressionReady,
    homeDistances,
    expansionRadius: profile.expansionRadius,
    round: selectRound(state),
    ruinsIncomeRoundLimit: selectRuinsIncomeRoundLimit(state),
    focusCaptureTargets,
    forcedCaptureByUnit: forcedTargetsForCampaigns,
    isAggressiveArchetype: profile.military > 1,
    // Волна 22 (21.9b): нейтральные города с путями влияния — вне кампаний
    excludedCaptureQuarters: campaignExcludedQuarters,
    // Волна 23 (22.3): FANATICISM — руины становятся целями с любого раунда
    includeRuinsCaptures: isPlayerPolicy(currentPlayer, POLICIES.FANATICISM),
    extraCaptureTargets: extraCaptureTargetsForCampaigns,
    // Волна 25 (23.1): без свободных отрядов оборона недоукомплектованного
    // города вправе перехватить отряд у захватной кампании
    allowDefenseCampaignSteal: true,
    // Волна 32 (оборона): риск по городам; null у legacy — прежний радиус 2
    cityCaptureThreats: w32CityThreats,
    // Волна 32 (логистика): подчасти кампаний; поле legacy-подчасти null, все три
    // legacy — null целиком (прежнее поведение). march (L3) — кампания отряда вне
    // снабжения и ожидание роста у края снабжения ради марша; stall (L4) — контроль
    // прогресса, отряды на жестком удержании вне захвата и осады; targetOrder (L5)
    w32Logistics: w32MarchActive || w32StallActive || w32TargetOrderActive
      ? {
        march: w32MarchActive
          ? {outOfSupplyUnitIds: w32OutOfSupplyUnitIds, supplyReachCells}
          : null,
        stall: w32StallActive
          ? {
            unassignableUnitIds: w32HeldUnitIds,
            maxStallRounds: W32_CAMPAIGN_MAX_STALL_ROUNDS,
            blacklistRounds: W32_CAMPAIGN_STALL_BLACKLIST_ROUNDS,
            growthHoldUnitIds: w32GrowthHoldUnitIds,
          }
          : null,
        targetOrder: w32TargetOrderActive,
      }
      : null,
    // Волна 32 (оборона): выдержки не занимают оборону чужого для них города —
    // независимо от гейта логистики (у legacy обороны null)
    defenseHeldUnitIds: w32CityThreats !== null ? w32HeldUnitIds : null,
    // Волна 33 (M6, только strongCities): в армейской фазе города ближайшего
    // противника оси — первыми среди захватных целей; после нее новые цели
    // дальше радиуса экспансии не назначаются, а существующие не сбрасываются.
    // null у всех остальных — прежнее поведение
    bonusStrongCities: strongCities && bonusAxis
      ? {enemyColor: bonusAxis.enemyColor, isArmyPhase: Boolean(isBonusArmyPhaseNow)}
      : null,
    // Волна 35 (G): гарнизоны вне кампаний (их кампании снимаются); null у legacy
    garrisonUnitIds: w35GarrisonByUnitId ? new Set(w35GarrisonByUnitId.keys()) : null,
    // Волна 35 (K): дальность захватных кампаний, города ближайшего агрессивного
    // соседа первыми, застревание у края снабжения; null у legacy
    w35CampaignRange: w35CampaignRangeActive
      ? {
        maxDistance: W35_CAPTURE_CAMPAIGN_MAX_DISTANCE,
        isFarAllowed: ownArmyStrength >= strongestOpponentTotal * W35_CAPTURE_CAMPAIGN_FAR_SUPERIORITY
          && ownArmyStrength > 0,
        neighborColor: w35AggressiveNeighbor ? w35AggressiveNeighbor.color : null,
        supplyReachCells,
      }
      : null,
    // Волна 37 (B1): города с резервным держателем на казарме — без обороны NEXT_ROUND
    w37ReserveCityKeys,
  });

  // Волна 32 (логистика, L3): BFS от конверта снабжения — тяга обратно для отрядов,
  // стоящих вне снабжения без разрешения марша. Один раз на тик и только если
  // среди еще не ходивших отрядов есть такой (у legacy null). В раунде 13 фаз
  // снабжения больше нет — голод не наступит, тянуть назад незачем
  const w32SupplyReturnDistances = w32MarchActive && supplyReachCells
    && selectRound(state) < FINAL_ROUND
    && playerUnits.some((unit) => !isUnitInsectoid(unit) && !supplyReachCells.has(unit.hexKey))
    ? calcBfsDistances([...supplyReachCells], distanceHexes, gridRenderer, W32_SUPPLY_RETURN_MAX_DISTANCE)
    : null;

  // Волна 32 (логистика, L3): гарнизоны городов — целей захватных кампаний (один раз на
  // тик, целей не больше, чем отрядов). Без снабжения (нет ферм или ECOANARCHISM)
  // ветка голода не срабатывает — считать нечего
  let w32CampaignTargetGarrisons = null;
  if (w32MarchActive && supplyReachCells) {
    w32CampaignTargetGarrisons = new Map();
    campaignByUnit.forEach((campaign) => {
      if (campaign.type !== CAMPAIGN_TYPES.CAPTURE
        || w32CampaignTargetGarrisons.has(campaign.targetHexKey)
      ) {
        return;
      }
      const cityInfo = enemyQuarters.get(campaign.targetHexKey);
      if (!cityInfo) {
        return;
      }
      const owner = players.find((player) => player.color === cityInfo.color);
      w32CampaignTargetGarrisons.set(campaign.targetHexKey, calcCityGarrisonStrength(
        cityInfo.city, owner ? owner.id : NEUTRAL_PLAYER_ID, units, gridRenderer
      ));
    });
  }

  // Крепости на карте: занятие свободной достроенной, оборона своей недостроенной,
  // блокировка недостроенной крепости противника (нюансы живого теста 10)
  const specialBuildingsForMovement = selectSpecialBuildings(state);
  const fortressesUnderConstruction = selectFortressesUnderConstruction(state) || [];
  const fortressCells = new Map();
  Object.entries(citiesCache || {}).forEach(([cityColor, colorCities]) => {
    (colorCities || []).forEach((city) => {
      getCityHexKeys(city).forEach((cellKey) => {
        if (specialBuildingsForMovement[cellKey] === SPECIAL_BUILDING_TOKENS.FORTRESS) {
          fortressCells.set(cellKey, {
            color: cityColor,
            isCompleted: !fortressesUnderConstruction.includes(cellKey),
          });
        }
      });
    });
  });

  // Отряды противника, блокирующие НАШИ ключевые точки и строения — приоритетные
  // цели атаки (ценность зависит от актива: Великие руины > руины/фермы > рудники)
  const enemyBlockedAssetValues = calcEnemyBlockedAssetValues(
    currentPlayer, units, citiesCache, specialBuildingsForMovement, gridRenderer
  );

  // Эльфы: выросшие отряды (d6 на максимуме, d10 >= 50% лимита) возвращаются
  // на свободную свою казарму под апгрейд — иначе рост встает, а снабжение
  // продолжает тратиться (живой тест 14)
  let elfUpgradeReturnUnits = null;
  let elfUpgradeReturnDistances = null;
  if (isPlayerFaction(currentPlayer, FACTIONS.ELVES)) {
    const grownUnits = playerUnits.filter((unit) => {
      if (ownBarracksCells.has(unit.hexKey)) {
        return false;
      }
      const maxSize = getUnitTypeConfig(unit.type)?.maxSize || 0;
      if (unit.type === UNIT_TYPES.D6.id) {
        return maxSize > 0 && unit.size >= maxSize;
      }
      if (unit.type === UNIT_TYPES.D10.id) {
        return unit.size >= maxSize * 0.5;
      }
      return false;
    });
    if (grownUnits.length > 0) {
      const freeBarracksCells = [...ownBarracksCells].filter((cellKey) =>
        !units.some((otherUnit) => otherUnit.hexKey === cellKey)
      );
      if (freeBarracksCells.length > 0) {
        elfUpgradeReturnUnits = new Set(grownUnits.map((unit) => unit.id));
        elfUpgradeReturnDistances = calcBfsDistances(freeBarracksCells, distanceHexes, gridRenderer);
      }
    }
  }

  // Волна 39: позиции соблазнителей суккубов и отрядов гоблинов (null у
  // остальных фракций и у legacy-игроков)
  const w39BuildingsDamage = selectBuildingsDamage(state);
  const w39SuccubesContext = createW39SuccubesMovementContext({
    player: currentPlayer, units, hexes, specialBuildings: specialBuildingsForMovement,
    buildingsDamage: w39BuildingsDamage, fortressesUnderConstruction, citiesCache,
  });
  const w39GoblinsContext = createW39GoblinsMovementContext({
    player: currentPlayer, players, hexes, citiesCache, specialBuildings: specialBuildingsForMovement,
    buildingsDamage: w39BuildingsDamage, fortressesUnderConstruction,
  });

  // Волна 40 (V3): дистанции до чужих городов для тяги выросших великанов
  const w40GiantAttackDistances = checkIfW40GiantsAttackPlayer(currentPlayer) && enemyQuarters.size > 0
    ? calcBfsDistances([...enemyQuarters.keys()], distanceHexes, gridRenderer)
    : null;

  const scoringContext = {
    w39SuccubesContext,
    w39GoblinsContext,
    w40GiantAttackDistances,
    units,
    hexes,
    currentPlayer,
    citiesCache,
    gridRenderer,
    enemyQuarters,
    ownQuarters,
    predictedEnemyCellSizes: predictions?.predictedEnemyCellSizes || null,
    predictedThreatenedOwnQuarters: predictions?.threatenedOwnQuarters || null,
    predictedVacatedEnemyQuarters: predictions?.vacatedEnemyQuarters || null,
    idleArmyFrontierDistances,
    // Надбавки бота с экономическим бонусом (null у обычных ботов)
    bonusProfile,
    isAggressionReady,
    // Волна 18 (17.6): атака значительно более крупного нейтрала слабым отрядом
    // разрешена только агрессивным фракциям или владельцам MILITARISM/BARBARISM
    weakNeutralAttackAllowed: hasMeleeBonusPolicy
      || WEAK_NEUTRAL_ATTACK_FACTIONS.has(currentPlayer.factionId)
      || checkIfW42CelectialsStormPlayer(currentPlayer, gameTypeForW37),
    isDwarvesPlayer: isPlayerFaction(currentPlayer, FACTIONS.DWARVES),
    targetDistances,
    ownQuartersUnderAttack,
    supplyReachCells,
    profile,
    campaignByUnit,
    campaignDistances,
    enemyKeyPointBlockValues,
    enemyBlockedAssetValues,
    // Давление: карты «ключ → множитель» (волна 33, M2; у обычных ботов 1.6 / 1)
    pressureFactorByPlayerId,
    pressureFactorByColor,
    pressureFactorByCell,
    pressureTargetDistances,
    fortressCells,
    isOutmatchedDefender,
    topOwnCityQuarters,
    isTopCityThreatened,
    demonAuraMaps,
    ownFarmCells: new Set(playerFarms),
    isDemonsPlayer,
    isRatmenPlayer,
    isInsectoidsPlayer,
    // Волна 22: города с фермой (21.12), центральные точки демонов (21.3),
    // нейтральные города с путями влияния вентори/просвещенных (21.9b)
    farmCityQuarters,
    uncontrolledCentralKeyPoints,
    discouragedCaptureQuarters: influenceConnectedNeutralQuarters,
    // Волна 25: кварталы под захватом следующим ходом (23.1) и «перезаписанные»
    // захваты пустых городов (23.8)
    // Волна 32 (оборона): у new-группы по-квартальный imminent заменен бонусом
    // держателя города; у legacy cityThreatByQuarter и attackedCityQuarters = null
    imminentThreatQuarters: cityThreatByQuarter ? null : imminentThreatQuarters,
    cityThreatByQuarter,
    attackedCityQuarters,
    oversubscribedCaptureQuarters,
    // Волна 22.1: кварталы города-фокуса роя — гарнизон фокуса исключен из
    // по-юнитных запретов боя с нейтралами (осада уже одобрена isCityTakeable)
    cityFocusQuarters,
    // Эльфы: свои казармы + бюджет на найм — для заблаговременного освобождения платформы
    ownBarracksCells,
    elfUpgradeReturnUnits,
    elfUpgradeReturnDistances,
    elfCanAffordHire: isPlayerFaction(currentPlayer, FACTIONS.ELVES)
      && currentPlayer.coins >= getPreferredUnitPrice(currentPlayer, selectRound(state), playerUnits, citiesCache),
    specialBuildings: specialBuildingsForMovement,
    // SERVARIANISM (любая фракция): свои рудники для расстановки отрядов 7+
    servarianismMineCells: isPlayerPolicy(currentPlayer, POLICIES.SERVARIANISM)
      ? new Set((citiesCache?.[currentPlayer.color] || []).flatMap((city) => [
        ...(city.mine || []),
        ...(city.super_mine || []),
      ]))
      : null,
    // Волна 27 (24.2, запечена): охота на эльфов-кайтеров — смежная позиция
    // навязывает ближний бой (сами эльфы под гейт не попадают)
    elfHuntActive: !isPlayerFaction(currentPlayer, FACTIONS.ELVES)
      && units.some((otherUnit) => otherUnit.ownerId !== currentPlayer.id
        && otherUnit.factionId === FACTIONS.ELVES),
    // Волна 27 (24.1, запечена): кавалерия роя поддерживает дерущихся
    // союзников смежностью (+2 урона каждому по способности фракции)
    ratmenAllySupportActive: isRatmenPlayer,
    // Волна 28 (25.3): бюджет ферм — не только геометрическая досягаемость.
    // Ревертируется гейтом волны 28 (легаси-группы играют по поведению волны 27)
    wave28FarmSupplyBudgetActive,
    budgetAwareSupplyReachCells: null,
    // Волна 32 (логистика, L3): null у legacy-группы — ветка марша не срабатывает.
    // Раунд — под отдельным ключом: context.round читает рост на казарме (живой
    // тест 16.3), его добавление включило бы то правило у всех вне гейта
    w32CampaignTargetGarrisons,
    w32Round: selectRound(state),
    w32SupplyReturnDistances,
    // Волна 33 (M7, «сильные города»): гейт, ось удара, армейская фаза и раунд под
    // отдельным ключом (context.round по-прежнему не задан — см. выше). Ниже —
    // данные новых правил: гарнизоны фронтирных городов (M4), отряды на удержании
    // роста и форсированные цели (не гарнизон и без тяги к кузнице, M3/M4),
    // градиент к кварталам ближайшего противника (M2). У остальных всё null/false
    strongCities: Boolean(strongCities && bonusAxis),
    bonusAxis: strongCities ? bonusAxis : null,
    isBonusArmyPhase: Boolean(isBonusArmyPhaseNow),
    bonusRound: strongCities && bonusAxis ? selectRound(state) : null,
    bonusGarrisonByQuarter,
    bonusGrowthHoldUnitIds: strongCities && bonusAxis ? w32GrowthHoldUnitIds : null,
    bonusThreatReleasedUnitIds,
    bonusForcedCaptureUnitIds: strongCities && bonusAxis ? new Set(forcedTargetsForCampaigns.keys()) : null,
    bonusTargetDistances,
    // Волна 35: гарнизоны по доктрине (G) и захват без выживания (D1); у legacy null/false
    w35GarrisonByUnitId,
    // G: режим удержания гарнизона ('hard' — фильтр клеток ниже и надбавка, 'soft' —
    // ценность держателя на кварталах своего города)
    w35GarrisonLockHard: isW35GarrisonLockHard,
    w35SuicideActive: isW35SuicideActive,
    // Гарнизон, отпущенный на текущем тике цикла ниже (checkIfW35GarrisonReleased)
    w35ReleasedGarrisonUnitId: null,
    // Волна 37 (боты «золото+»; у legacy и остальных ботов null): B1 — казарма →
    // резервный держатель и запрет кварталов (collectW37BarracksStay); B1/T — записи
    // городов с фантомным наймом и доля ценности держателя; B2 — данные роста на
    // казармах у лагеря и отряды на удержании роста (вне тяги к фронту)
    w37BarracksStayByHexKey,
    w37RecruitHold: isW37RecruitHoldActive
      ? {threatByQuarter: w37ThisRound.threatByQuarter, factor: w37Knobs.recruitHolderFactor}
      : null,
    w37CampHold: isW37CampHoldActive
      ? {
        barracksInfo: w37BarracksInfo,
        growthPerSize: w37Knobs.growthPerSize,
        isSupplyEverywhere: isW37SupplyEverywhere,
        isFinalRound: isW37FinalRound,
      }
      : null,
    w37CampHoldUnitIds,
  };

  let bestMove = null;
  let bestImprovement = 0;
  // Волна 35 (G): последний раунд — голод гарнизону не грозит (только у G-активных)
  const isW35FinalRound = w35GarrisonByUnitId ? checkIfFinalRound(state) : false;

  for (const unit of playerUnits) {
    // Волна 28 (25.3): бюджет ферм для ЭТОГО отряда — сам он исключен из
    // чужих претензий (его текущая позиция не должна занимать слот сама у себя)
    scoringContext.budgetAwareSupplyReachCells = calcBudgetAwareSupplyReachCells(unit.id);

    const {cells, additionalSupplyCells, paths} = calculateAvailableMovementCells(
      gridRenderer, hexes, units, unit, players, trapDeathHexKeys
    );
    // Волна 35 (G): в режиме 'hard' гарнизон ходит только по кварталам своего города,
    // в 'soft' — куда угодно (держит его ценность держателя в оценке). Казарменные
    // выдержки не мешают ему встать на квартал своего города (он смежен казарме).
    // Обреченный в своем бою или голодающий гарнизон на этом тике отпускается
    // (checkIfW35GarrisonReleased) — и фильтр клеток, и оценка гарнизона снимаются
    const w35AssignedGarrisonCity = w35GarrisonByUnitId ? w35GarrisonByUnitId.get(unit.id) : undefined;
    const isW35GarrisonReleased = Boolean(w35AssignedGarrisonCity) && checkIfW35GarrisonReleased({
      unit,
      garrisonCity: w35AssignedGarrisonCity,
      cells,
      units,
      supplyCells: scoringContext.budgetAwareSupplyReachCells || supplyReachCells,
      isFinalRound: isW35FinalRound,
    });
    if (w35GarrisonByUnitId) {
      scoringContext.w35ReleasedGarrisonUnitId = isW35GarrisonReleased ? unit.id : null;
    }
    const w35GarrisonCity = isW35GarrisonReleased ? undefined : w35AssignedGarrisonCity;
    const stayScore = scoreUnitPosition(unit, unit.hexKey, scoringContext);

    // Волна 32 (оборона): исключение из казарменных выдержек проверяем только у
    // удерживаемых отрядов (у legacy false)
    const isW32HeldUnit = cityThreatByQuarter !== null
      && (leprechaunHoldUnits.has(unit.id) || elfPatriotismHoldUnits.has(unit.id) || d12HoldUnits.has(unit.id));
    // Волна 37 (B1, ручка W37_BARRACKS_STAY_HARD): кварталы городов, на которые
    // отряд с этой казармы без угрозы этого раунда не встает (рост на казарме
    // теряется, а город держит резерв или свой отряд на квартале). У legacy null
    const w37StayEntry = w37BarracksStayByHexKey && w37Knobs.isStayHard
      ? w37BarracksStayByHexKey.get(unit.hexKey)
      : undefined;
    const w37BlockedQuarterKeys = w37StayEntry && w37StayEntry.unitId === unit.id
      ? w37StayEntry.blockedQuarterKeys
      : null;

    for (const cellKey of cells) {
      const isW35GarrisonQuarterCell = Boolean(w35GarrisonCity) && w35GarrisonCity.quarters.has(cellKey);
      if (w35GarrisonCity && isW35GarrisonLockHard && !isW35GarrisonQuarterCell) {
        continue;
      }
      // Волна 37 (B1): квартал своего города без угрозы этого раунда — только ради
      // обороны в этом ходу: с него по матожиданию убивается смежный живой враг
      if (w37BlockedQuarterKeys && w37BlockedQuarterKeys.has(cellKey)
        && !getNeighborEnemyUnits(unit.ownerId, cellKey, units).some((enemy) => enemy.ownerId !== NEUTRAL_PLAYER_ID
          && enemy.size <= getBotExpectedMeleeDamage(unit, unit.ownerId))
      ) {
        continue;
      }
      // Волна 32: встать на квартал своего города, который иначе захватят в конце
      // раунда, — исключение для всех выдержек (лепреконы, эльфы-PATRIOTISM, d12)
      const isW32CityGuard = isW32HeldUnit
        && cityThreatByQuarter.get(cellKey)?.level === CITY_THREAT_LEVELS.THIS_ROUND
        && calcCityHolderBonus(cityThreatByQuarter.get(cellKey), unit, cellKey) > 0;
      // Лепреконская казарменная выдержка: недорощенный d10/d20 покидает
      // казарму только ради гарантированного захвата незащищенного квартала
      if (leprechaunHoldUnits.has(unit.id)
        && !isW32CityGuard
        && !isW35GarrisonQuarterCell
        && !(enemyQuarters.has(cellKey)
          && !units.some((otherUnit) => otherUnit.ownerId !== currentPlayer.id
            && otherUnit.ownerId !== NEUTRAL_PLAYER_ID
            && calcHexDistance(otherUnit.hexKey, cellKey) <= 1))
      ) {
        continue;
      }
      // Волна 23 (22.6): эльфийская казарменная выдержка PATRIOTISM —
      // отряд < 60% максимума не покидает казарму вовсе
      if (elfPatriotismHoldUnits.has(unit.id) && !isW32CityGuard && !isW35GarrisonQuarterCell) {
        continue;
      }
      // Волна 19 (18.12): свежий d12 сходит с казармы только на гарантированный
      // захват (незащищенный чужой квартал) или на оборону своего города
      if (d12HoldUnits.has(unit.id) && !isW35GarrisonQuarterCell) {
        const isSafeCapture = enemyQuarters.has(cellKey)
          && !units.some((otherUnit) => otherUnit.ownerId !== currentPlayer.id
            && otherUnit.ownerId !== NEUTRAL_PLAYER_ID
            && calcHexDistance(otherUnit.hexKey, cellKey) <= 1);
        const isCityDefense = ownQuartersUnderAttack.size > 0
          && (ownQuarters.has(cellKey)
            || getNeighborHexKeys(cellKey, gridRenderer)
              .some((adjKey) => ownQuartersUnderAttack.has(adjKey)));
        if (!isSafeCapture && !isCityDefense && !isW32CityGuard) {
          continue;
        }
      }
      const cellScore = scoreUnitPosition(unit, cellKey, scoringContext);
      const improvement = cellScore - stayScore;
      if (improvement > bestImprovement) {
        bestImprovement = improvement;
        bestMove = {
          unit,
          cellKey,
          usesAdditionalSupply: additionalSupplyCells.includes(cellKey),
          path: paths[cellKey],
        };
      }
    }
  }

  // Лепреконы: кража монет вместо перемещения. Цель — лидер по очкам+монетам
  // (задавить экономически того, кто вырывается вперед); при слабой армии кража
  // приоритетнее еще сильнее — рой копит и ворует, а не марширует
  if (isLeprechaunsPlayer) {
    // Приоритетная цель кражи — владелец города активной захватной кампании:
    // будущую жертву нападения обедняем заранее (живой тест 14)
    let campaignStealTarget = null;
    for (const campaign of campaignByUnit.values()) {
      if (campaign.type !== CAMPAIGN_TYPES.CAPTURE) {
        continue;
      }
      const cityInfo = enemyQuarters.get(campaign.targetHexKey);
      if (!cityInfo || cityInfo.color === NEUTRAL_PLAYER_COLOR) {
        continue;
      }
      const owner = players.find((player) => player.color === cityInfo.color);
      if (owner && owner.coins > 0) {
        campaignStealTarget = owner;
        break;
      }
    }

    const stealTarget = campaignStealTarget || players
      .filter((player) => player.id !== currentPlayer.id && player.coins > 0)
      .reduce((best, player) => {
        const playerScore = (player.winPoints || 0) + (player.coins || 0) / 2;
        const bestScore = best ? (best.winPoints || 0) + (best.coins || 0) / 2 : -1;
        return playerScore > bestScore ? player : best;
      }, null);

    if (stealTarget) {
      const strongestOpponentArmy = calcStrongestOpponentArmy(currentPlayer.id, players, units);
      const isEconomicMode = ownArmyStrength < strongestOpponentArmy;

      // playerUnits уже исключает и перемещенных, и кравших в этой фазе.
      // Сортировка по убыванию размера: крупный отряд ворует первым и может
      // выбрать цель "досуха", тогда мелкие отряды на следующих вызовах либо
      // найдут другую цель с монетами, либо (если красть больше не у кого)
      // корректно уйдут в обычное перемещение вместо кражи 1 монеты впустую
      const stealCandidateUnits = [...playerUnits].sort((a, b) => b.size - a.size);
      for (const unit of stealCandidateUnits) {
        // Живой тест 16.9: дорощенный d10 (размер 9+) на своей казарме не
        // ворует — его действие фазы ценнее как выход в атаку
        if (unit.type === UNIT_TYPES.D10.id
          && unit.size >= 9
          && ownBarracksCells.has(unit.hexKey)
        ) {
          continue;
        }
        // Волна 35 (G): гарнизон, еще не стоящий на квартале своего города, не
        // ворует — его действие фазы ушло бы мимо квартала
        const stealGarrisonCity = w35GarrisonByUnitId ? w35GarrisonByUnitId.get(unit.id) : undefined;
        if (stealGarrisonCity && !stealGarrisonCity.quarters.has(unit.hexKey)) {
          continue;
        }
        const stealValue = Math.min(
          stealTarget.coins,
          integerDivision(unit.size, LEPRECHAUNS_UNIT_SIZE_FOR_1_COIN_THEFT)
        );
        if (stealValue <= 0) {
          continue;
        }
        const stealScore = stealValue * BOT_WEIGHTS.LEPRECHAUN_STEAL_VALUE_FACTOR
          * (isEconomicMode ? 1.5 : 1);
        if (!bestMove || stealScore > bestImprovement) {
          store.dispatch(leprechaunsStealCoins(unit.id, stealTarget.id, stealValue));
          return true;
        }
      }
    }
  }

  if (!bestMove) {
    return false;
  }

  const dispatchMove = () => {
    store.dispatch(moveUnit(bestMove.unit.id, bestMove.unit.type, bestMove.cellKey));
    store.dispatch(addPendingMovementAction(
      bestMove.unit.id,
      bestMove.unit.hexKey,
      bestMove.cellKey,
      bestMove.usesAdditionalSupply,
    ));
  };

  // Ловушки Гоблинов должны срабатывать и на ботах, включая мгновенное
  // применение хода в store.__isBotSandbox (фоновые симуляции сильного
  // режима/планировщика), которое полностью минует animateUnitMovement ниже
  // — резолвер вызывается ЗДЕСЬ, до выбора анимированного/мгновенного пути,
  // единой точкой правды с человеческим кликом (см. GameScene.handleMovementPhaseClick,
  // add-goblins-faction design.md Decision 4)
  const stateForTrapCheck = store.getState();
  const goblinsTraps = selectGoblinsTraps(stateForTrapCheck);
  let resolution = null;
  if (goblinsTraps.length > 0 && bestMove.path) {
    const turnOrder = selectTurnOrder(stateForTrapCheck);
    const candidateResolution = resolveMovementWithTraps(bestMove.path, bestMove.unit, goblinsTraps, turnOrder);
    if (candidateResolution.triggeredTrapEvents.length > 0) {
      resolution = candidateResolution;
    }
  }

  const renderer = store.__isBotSandbox
    ? null
    : REGISTRY_SINGLETON.getHexGridRenderer && REGISTRY_SINGLETON.getHexGridRenderer();

  if (resolution) {
    const truncatedPath = bestMove.path.slice(0, resolution.finalStepIndex + 1);
    const dispatchTrapMovement = () => {
      store.dispatch(applyGoblinsTrapMovement(bestMove.unit.id, {
        ...resolution,
        usesAdditionalSupply: bestMove.usesAdditionalSupply,
        ownerId: bestMove.unit.ownerId,
        unitType: bestMove.unit.type,
        unitSizeBeforeDamage: bestMove.unit.size,
        // Для правильного фракционного скина призрака у наблюдателей
        // (resolveUnitImageKey, см. GameScene.js - тот же фикс)
        unitFactionId: bestMove.unit.factionId,
        unitCapturedFromOwnerId: bestMove.unit.capturedFromOwnerId ?? null,
      }));

      // Индикаторы на поле - для наблюдателей (не блокирует ход бота, см.
      // design.md Decision 21): blocking: false, ничего не показывает и не
      // требует клика. В store.__isBotSandbox (renderer === null) нет ни
      // сцены, ни смысла - фоновая симуляция ничего не рисует
      if (renderer) {
        // Реальная потеря численности (см. GameScene.js, тот же фикс) -
        // не сумма сырого урона по ловушкам, у Орков это разные числа
        const totalDamageTaken = bestMove.unit.size - resolution.remainingSize;
        store.dispatch(showGoblinsTrapKillNotice({
          unit: {...bestMove.unit},
          died: resolution.unitDied,
          finalHexKey: resolution.finalHexKey,
          totalDamageTaken,
          triggeredTrapEvents: resolution.triggeredTrapEvents,
          blocking: false,
        }));
        // Таймера скрытия нет: индикаторы срабатывания на поле живут до конца
        // этапа (сброс по COMPLETE_PHASE/SET_WAR_PHASE_STAGE)
      }
    };

    if (renderer && renderer.animateUnitMovement && !renderer.isAnimatingMovement) {
      renderer.animateUnitMovement(bestMove.unit.id, truncatedPath, dispatchTrapMovement);
    } else {
      dispatchTrapMovement();
    }

    return true;
  }

  if (renderer && renderer.animateUnitMovement && bestMove.path && !renderer.isAnimatingMovement) {
    renderer.animateUnitMovement(bestMove.unit.id, bestMove.path, dispatchMove);
  } else {
    dispatchMove();
  }

  return true;
};
